/* FLM — Discover, pixel-faithful port of the final canvas artboards:
   A · Classic stack (ScrSwipeClassic)  B · The Salon (ScrSwipeSalon)
   C · Orbit (ScrSwipeOrbit)            Match moment (ScrMatch)
   Who chose you (ScrLikes)             Viewing Sasha (ScrProfileOther)
   Export contract unchanged: { DiscoverScreen, MatchOverlay, ProfileDetail }. */
import React from 'react';
import { Icon, GradPhoto, Avatar, MonoLabel, ScreenHead, RoundBtn, Sheet, FullOverlay, VoiceWave, GlowBlob, Button, SparkBurst } from '../components/ui';
import { ReportSheet, ConfirmSheet } from '../components/report-sheet';
import { FLM_DATA, LIKES_YOU } from '../data/mock';
import { isDemo } from '../lib/demo';
import { haptic } from '../lib/telegram';
import { StatePanel, DeckCardSkeleton, GridSkeleton } from '../lib/feedback';
import { getDeck, postSwipe, rewindSwipe, getLikes, getLikesCount, getLikesPreview, getMe, getProfile, getSubscription, getDaily, claimDaily, putPreferences, mediaUrl, blockUser, reportUser, sendMessageRequest, ApiCodedError } from '../api/client';
import { registerBlur } from '../lib/blur';
import { attrValueLabel } from './profile';

/* Salon + Orbit are hidden for now (owner feedback: buggy). Flip this to bring
   the DECK|SALON|ORBIT switcher back — the views below stay intact. */
const MODES_ENABLED = false;

/* Distance sentinel — the filter slider's top stop persists this value and the
   server reads distanceKm >= 20000 as "Global": no distance filtering at all. */
const GLOBAL_KM = 20000;

/* OWNER DECISION: deck action row = Rewind · Pass · Like · Spark. Star is gone
   (redundant) but kept behind this flag so the code path survives; Gift moved
   into ProfileDetail. Flip to true to restore the Star super-like in the row. */
const STAR_ENABLED = false;

/* OWNER DECISION (this round): gifts are hidden app-wide. ProfileDetail's gift
   button (in the action row) + the "send a gift from chat once you match" hint
   (giftAfterMatch) are gated behind this flag so the code path survives untouched.
   With it off, ProfileDetail's action set is Pass · Like · Spark (no Gift). Flip
   to true to bring gifting back. */
const GIFTS_ENABLED = false;

/* ---------- helpers ---------- */

/* Tiny client-side ISO-3166 alpha-2 → display name map for the card/detail
   location fallback (the API stores country as ISO-2). Mirrors the common codes
   in the API's geo/country-centroids.ts; unknown codes fall back to the raw code
   so we never render a blank. Read-only, no import coupling to the API. */
const COUNTRY_NAMES = {
  GB: 'United Kingdom', IE: 'Ireland', FR: 'France', DE: 'Germany', ES: 'Spain', PT: 'Portugal',
  IT: 'Italy', NL: 'Netherlands', BE: 'Belgium', CH: 'Switzerland', AT: 'Austria', SE: 'Sweden',
  NO: 'Norway', DK: 'Denmark', FI: 'Finland', PL: 'Poland', CZ: 'Czechia', GR: 'Greece',
  RO: 'Romania', HU: 'Hungary', UA: 'Ukraine', RU: 'Russia', TR: 'Türkiye',
  AE: 'United Arab Emirates', SA: 'Saudi Arabia', IL: 'Israel', IR: 'Iran', IQ: 'Iraq',
  QA: 'Qatar', KW: 'Kuwait', JO: 'Jordan', LB: 'Lebanon',
  EG: 'Egypt', MA: 'Morocco', DZ: 'Algeria', TN: 'Tunisia', NG: 'Nigeria', ZA: 'South Africa',
  KE: 'Kenya', GH: 'Ghana', ET: 'Ethiopia',
  CN: 'China', JP: 'Japan', KR: 'South Korea', IN: 'India', PK: 'Pakistan', BD: 'Bangladesh',
  ID: 'Indonesia', MY: 'Malaysia', SG: 'Singapore', TH: 'Thailand', VN: 'Vietnam', PH: 'Philippines',
  AF: 'Afghanistan', KZ: 'Kazakhstan', UZ: 'Uzbekistan', AZ: 'Azerbaijan', GE: 'Georgia',
  AM: 'Armenia', LK: 'Sri Lanka', HK: 'Hong Kong', TW: 'Taiwan',
  AU: 'Australia', NZ: 'New Zealand',
  US: 'United States', CA: 'Canada', MX: 'Mexico',
  GT: 'Guatemala', CR: 'Costa Rica', PA: 'Panama', CU: 'Cuba', DO: 'Dominican Republic',
  BR: 'Brazil', AR: 'Argentina', CL: 'Chile', CO: 'Colombia', PE: 'Peru', VE: 'Venezuela',
  EC: 'Ecuador', UY: 'Uruguay',
};
const countryName = (code) => (code ? COUNTRY_NAMES[String(code).toUpperCase()] || code : null);

/* The card / detail location line, owner's rules:
   - distance is a number → "<City> · <N> km away" if a city exists, else "<N> km away"
   - no distance (viewer or target has no stored coords) → "<City>, <Country>" if
     available, else just the city, else "Location hidden". Never a bare "— km away".
   Returns a string. `dist` is the prototype card field (number, or '—' sentinel). */
function locationLine(p, t, faNum) {
  const hasDist = typeof p.dist === 'number' && Number.isFinite(p.dist);
  const cityName = p.city || null;
  const place = [cityName, countryName(p.country)].filter(Boolean).join(', ');
  if (hasDist) {
    return cityName
      ? t('cityKmAway', { city: cityName, n: faNum(p.dist) })
      : t('kmAway', { n: faNum(p.dist) });
  }
  return place || t('locationHidden');
}

/* Subtle gold-crown premium badge — reused on the deck card and ProfileDetail.
   Mirrors the FLM Black pill used near the profile name (hairline accent + gold
   crown), kept small so it never competes with the name/match ring. */
function PremiumBadge({ t, style = {} }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none',
      padding: '2px 8px', borderRadius: 99,
      border: '1px solid rgba(255,212,145,0.4)', background: 'rgba(255,212,145,0.12)',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      ...style,
    }}>
      <Icon name="crown" size={11} color="var(--gold)" fill="var(--gold)" />
      <span className="flm-mono" style={{ fontSize: 8.5, color: 'var(--accent)' }}>{t('premiumBadge')}</span>
    </span>
  );
}

/* Deterministic match % from mock fields (canvas shows 87 / 81 / 78). */
/* Cosmetic compatibility score. Real signals (shared interests, verified,
   online) lift it, but a DETERMINISTIC per-person spread keeps cards from all
   showing the same floor when there's nothing in common (the old base-64 made
   every imported profile read exactly 64%). Stable per person → same % each time. */
const matchPct = (p) => {
  // Deck cards now carry a REAL match % from the server (weighted compatibility:
  // shared interests + age proximity + relationship goal + languages + lifestyle).
  // Other surfaces (matches/likes lists) don't, so fall back to the deterministic
  // cosmetic spread there so the number is still stable per person.
  if (typeof p.matchPct === 'number') return p.matchPct;
  const spread = hashOf(p.userId || p.id || p.name || '') % 24; // 0..23, stable
  const score = 58 + spread + (p.common || 0) * 6 + (p.verified ? 4 : 0) + (p.online ? 3 : 0);
  return Math.max(55, Math.min(98, score));
};

/* Voice duration text — same "0:0X" look, guarded for real takes ≥10s. */
const fmtVoice = (s) => '0:' + String(Math.max(0, Math.floor(Number(s) || 0))).padStart(2, '0');

/* Canvas Verified badge — accent disc + black check (.flm-verif in tokens.css). */
function Verif({ size = 17 }) {
  return (
    <span className="flm-verif" style={{ width: size, height: size }}>
      <Icon name="check" size={size * 0.58} strokeWidth={3} />
    </span>
  );
}

/* Photo story segments along the top of a card (canvas artboards A + Viewing Sasha).
   Now REAL: `active` is the photo on screen — bright, with earlier ones half-lit
   and later ones dim (stories pattern). Single-photo cards hide the rail. */
function PhotoSegments({ total = 4, active = 0 }) {
  if (total <= 1) return null;
  return (
    <span style={{ position: 'absolute', top: 12, left: 16, right: 16, display: 'flex', gap: 5, zIndex: 3 }}>
      {Array.from({ length: total }).map((_, i) => (
        <i key={i} style={{ flex: 1, height: 3, borderRadius: 99, transition: 'background 0.2s ease', background: i === active ? '#fff' : i < active ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.25)' }}></i>
      ))}
    </span>
  );
}

/* Conic match-% ring, top-right of the deck card. */
function MatchRing({ value, faNum }) {
  return (
    <span style={{
      position: 'absolute', top: 26, right: 16, zIndex: 3, width: 46, height: 46, borderRadius: '50%',
      background: `conic-gradient(var(--accent) 0 ${value}%, rgba(255,255,255,0.15) ${value}% 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{
        width: 38, height: 38, borderRadius: '50%', background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', fontWeight: 600,
      }}>{faNum(value)}</span>
    </span>
  );
}

/* Round action button that supports filled icons (deck row, detail floating row).
   Tactile feedback on activate (owner testing): a quick spring scale-press
   (down to .88 → settles back via --ease-spring) plus a one-shot ring pulse in
   the button's own accent color. `ring` is that pulse color (Like=accent,
   Spark=love, Pass=muted, Rewind=gold); both are CSS-only so the global
   prefers-reduced-motion reset (tokens.css) collapses them to ~0ms for free. */
function ActionBtn({ icon, size = 52, iconSize = 20, color, bg = 'var(--surface)', border = '1px solid var(--border-bright)', glow = false, fill = false, ring, crown = false, onClick, style = {} }) {
  const [pressed, setPressed] = React.useState(false);
  const [pulse, setPulse] = React.useState(0); // bump → re-trigger the ring keyframe
  const ringColor = ring || (typeof color === 'string' && color !== '#000' ? color : 'var(--accent)');
  const fire = () => {
    setPulse((n) => n + 1);
    if (onClick) onClick();
  };
  return (
    <button
      className="flm-ghost-btn"
      onClick={fire}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        position: 'relative', width: size, height: size, borderRadius: '50%', border, background: bg, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', padding: 0,
        boxShadow: glow ? 'var(--glow-accent)' : 'none', cursor: 'pointer',
        transform: pressed ? 'scale(0.88)' : 'scale(1)',
        transition: 'transform 0.26s var(--ease-spring)',
        ...style,
      }}>
      {/* one-shot expanding ring in the button's accent — keyed on `pulse` so each
          tap remounts a fresh flmRingPulse (the same keyframe Orbit + the likes
          heart use); pointer-events:none so it never blocks the tap. */}
      {pulse > 0 ? (
        <span key={pulse} aria-hidden="true" style={{
          position: 'absolute', inset: -2, borderRadius: '50%', pointerEvents: 'none',
          border: '2px solid ' + ringColor,
          animation: 'flmRingPulse 0.5s var(--ease-out-expo)',
        }}></span>
      ) : null}
      <Icon name={icon} size={iconSize} fill={fill ? 'currentColor' : 'none'} />
      {/* tiny gold crown badge — the "premium" mark, clipped to the corner. Matches
          PremiumMessageBtn's badge exactly so premium actions (Message, Rewind) read
          consistently. pointer-events handled by the parent button (decorative). */}
      {crown ? (
        <span aria-hidden="true" style={{
          position: 'absolute', top: -3, insetInlineEnd: -3,
          width: 18, height: 18, borderRadius: '50%',
          background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1.5px solid #000', boxShadow: '0 0 8px rgba(255,212,145,0.6)',
        }}>
          <Icon name="crown" size={9} color="#000" fill="#000" />
        </span>
      ) : null}
    </button>
  );
}

/* Premium "Message before match" round action button — sits in the deck + detail
   action rows next to Pass/Like/Spark, matching their round-button language but
   wearing a distinct PREMIUM look: a gold-tinted face + gold hairline ring. Reads
   clearly as the paid reach-out without shouting over Like (it's a satellite, like
   Rewind/Spark). Shown to everyone — non-entitled taps route to the upgrade flow
   (it doubles as an upsell), so visibility is universal and the gate lives in the
   tap handler.
   `locked` (default true) controls the tiny gold CROWN badge in the corner: shown
   ONLY when the viewer does NOT hold the message-before-match perk (the feature is
   gated). Entitled (Infinity) viewers pass locked={false} → no crown, the button
   simply works as the compose entry (owner ask: stop crowning a feature you own). */
function PremiumMessageBtn({ t, size = 56, iconSize = 21, locked = true, onClick }) {
  const [pressed, setPressed] = React.useState(false);
  const [pulse, setPulse] = React.useState(0);
  const fire = () => { setPulse((n) => n + 1); if (onClick) onClick(); };
  return (
    <button
      className="flm-ghost-btn"
      onClick={fire}
      aria-label={t('messageBeforeMatch')}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        position: 'relative', width: size, height: size, borderRadius: '50%', flex: 'none', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        color: 'var(--gold)',
        // gold-tinted face + gold hairline ring = the premium signal (mirrors the
        // FLM Black gold accent; not gaudy — a single tint + ring).
        background: 'rgba(255,212,145,0.12)',
        border: '1px solid rgba(255,212,145,0.55)',
        boxShadow: '0 0 16px rgba(255,212,145,0.22)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        transform: pressed ? 'scale(0.88)' : 'scale(1)',
        transition: 'transform 0.26s var(--ease-spring)',
      }}>
      {/* one-shot gold ring pulse on tap (same flmRingPulse the other buttons use) */}
      {pulse > 0 ? (
        <span key={pulse} aria-hidden="true" style={{
          position: 'absolute', inset: -2, borderRadius: '50%', pointerEvents: 'none',
          border: '2px solid var(--gold)', animation: 'flmRingPulse 0.5s var(--ease-out-expo)',
        }}></span>
      ) : null}
      <Icon name="message" size={iconSize} color="var(--gold)" />
      {/* tiny crown badge — the "premium" mark, clipped to the corner. Shown ONLY
          when locked (viewer lacks the perk); entitled viewers see a clean button. */}
      {locked ? (
        <span aria-hidden="true" style={{
          position: 'absolute', top: -3, insetInlineEnd: -3,
          width: 18, height: 18, borderRadius: '50%',
          background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1.5px solid #000', boxShadow: '0 0 8px rgba(255,212,145,0.6)',
        }}>
          <Icon name="crown" size={9} color="#000" fill="#000" />
        </span>
      ) : null}
    </button>
  );
}

/* Shared "message before match" controller — the compose Sheet + send/403/409
   handling lifted so BOTH the deck row and ProfileDetail can open it without
   rebuilding the flow. `useMessageCompose({ t, onUpgrade })` returns:
     open(targetProfile)  — open the sheet for that card (premium gate is upstream)
     sheet                — the <Sheet> element to render (renders nothing when shut)
   Behavior: success → "Request sent" → auto-close; 403 auth/forbidden → onUpgrade();
   409/coded → inline "already connected/requested" note; bare network error → quiet. */
function useMessageCompose({ t, onUpgrade }) {
  const [target, setTarget] = React.useState(null); // the profile being messaged
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null); // null | 'sent' | 'exists'
  const open = (profile) => {
    if (!profile?.userId) return;
    haptic('light');
    setTarget(profile); setText(''); setBusy(false); setResult(null);
  };
  const close = () => { if (!busy) { setTarget(null); setText(''); setResult(null); } };
  const send = () => {
    const body = text.trim();
    if (!target?.userId || !body || busy) return;
    setBusy(true); setResult(null); haptic('light');
    sendMessageRequest(target.userId, body)
      .then(() => {
        haptic('success');
        setResult('sent');
        setTimeout(() => { setTarget(null); setText(''); setResult(null); }, 1400);
      })
      .catch((err) => {
        const code = err instanceof ApiCodedError ? err.code : null;
        if (code === 'auth/forbidden') { setTarget(null); onUpgrade && onUpgrade(); return; }
        // coded 4xx (e.g. 409 already matched/requested/blocked) → "already connected"
        // note; a bare network error has no code → stay quiet.
        if (code) setResult('exists');
      })
      .finally(() => setBusy(false));
  };
  const sheet = (
    <Sheet open={Boolean(target)} onClose={close}>
      {target ? (
        <React.Fragment>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.3rem', color: 'var(--text)', margin: '2px 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="crown" size={16} color="var(--gold)" fill="var(--gold)" />
            {t('messageComposeTitle', { name: target.name })}
          </h3>
          <p style={{ margin: '0 0 14px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)', lineHeight: 1.45 }}>{t('messageComposeHint')}</p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 500))}
            placeholder={t('messageComposePh')}
            rows={4}
            autoFocus
            style={{
              width: '100%', resize: 'none', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 14,
              background: 'var(--surface-elevated)', border: '1px solid var(--border-bright)', color: 'var(--text)',
              fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.5, outline: 'none',
            }}
          />
          {result === 'sent' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 0', color: 'var(--accent)', fontFamily: 'var(--font-body)', fontSize: 13.5 }}>
              <Icon name="check" size={15} strokeWidth={2.5} color="var(--accent)" />
              {t('messageRequestSent')}
            </div>
          ) : result === 'exists' ? (
            <div style={{ margin: '12px 0 0', color: 'var(--text-3)', fontFamily: 'var(--font-body)', fontSize: 13 }}>{t('messageAlready')}</div>
          ) : null}
          <Button variant="primary" onClick={send} disabled={busy || !text.trim() || result === 'sent'} style={{ width: '100%', marginTop: 16, gap: 8 }}>
            <Icon name="send" size={16} fill="currentColor" color="none" />
            {busy ? t('verifying') : t('messageSendCta')}
          </Button>
        </React.Fragment>
      ) : null}
    </Sheet>
  );
  return { open, sheet };
}

/* Header "See who likes you" heart — the headline premium upsell. When people
   have liked you it GLOWS (accent pulse) and shows a small accent count badge;
   at zero it's a plain ghost heart. Tapping always opens the LikesOverlay. */
function LikesHeartBtn({ count = 0, faNum, t, onClick }) {
  const has = count > 0;
  return (
    <button
      onClick={onClick}
      className="flm-round-btn"
      aria-label={t('revealLikes')}
      style={{
        position: 'relative', width: 36, height: 36, borderRadius: '50%', flex: 'none', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        border: '1px solid ' + (has ? 'rgba(255,212,145,0.55)' : 'var(--border-bright)'),
        background: has ? 'var(--accent-tint)' : 'var(--surface-elevated)',
        color: 'var(--accent)',
        /* flmPulseDot pulses an accent box-shadow ring (no scale/fade of the
           button itself) — a steady glow that draws the eye to the upsell. */
        animation: has ? 'flmPulseDot 2.2s var(--ease-out-expo) infinite' : 'none',
        transition: 'box-shadow 0.3s ease, background 0.3s ease, border-color 0.3s ease',
      }}
    >
      <Icon name="heart" size={17} fill={has ? 'var(--accent)' : 'none'} />
      {has ? (
        <span style={{
          position: 'absolute', top: -5, insetInlineEnd: -5,
          minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
          background: 'var(--accent)', color: '#000',
          fontFamily: 'var(--font-mono)', fontSize: '0.58rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 10px rgba(255,212,145,0.7)', border: '1.5px solid #000',
        }}>{faNum(count > 99 ? '99+' : count)}</span>
      ) : null}
    </button>
  );
}

/* ---------- Daily-limits HUD (owner testing: "show me what's left") ----------
   Reads GET /v1/me/subscription once and surfaces remaining daily allowance as
   small mono pills under the header: Sparks for everyone, plus likes-left for
   FREE users (premium with unlimited likes shows no likes counter). DEFENSIVE
   by design — a CONCURRENT agent is finalizing the {entitlements, usage} shape,
   so every field is optional-chained and the hook simply returns null (renders
   nothing, no crash) when the call fails, demo is on, or the fields aren't there
   yet. When a limit is hit the existing rate_limited→onUpgrade routing owns the
   paywall; this is purely an at-a-glance indicator. */
function useEntitlements(demo) {
  const [sub, setSub] = React.useState(null);
  const load = React.useCallback(() => {
    if (demo) return;
    getSubscription().then((r) => setSub(r || null)).catch(() => setSub(null));
  }, [demo]);
  React.useEffect(() => { load(); }, [load]);
  // Optimistically count a just-spent like/spark so the HUD + exhausted gates
  // flip INSTANTLY — before the async refresh resolves. Without this there's a
  // lag window where the count still reads "1 left" and the button still fires,
  // so the over-limit swipe goes through and only the server bounces it to the
  // paywall. Matches the backend tally: yesToday counts yes+spark, sparkToday
  // counts spark only. refreshSub() reconciles to the authoritative numbers.
  const bumpUsage = React.useCallback((dir) => {
    if (dir !== 'like' && dir !== 'spark') return;
    setSub((s) => {
      if (!s) return s;
      const usage = s.usage || {};
      const rc = s.recharge;
      // Drip buckets are per-kind now: a like spends the like bucket, a spark the
      // spark bucket. Decrement the matching balance so the pill + exhausted gate
      // flip instantly; load() reconciles to the authoritative numbers after.
      const dec = (b) => (b ? { ...b, balance: Math.max(0, (Number(b.balance) || 0) - 1) } : b);
      return {
        ...s,
        usage: {
          ...usage,
          yesToday: (Number(usage.yesToday) || 0) + 1,
          sparkToday: (Number(usage.sparkToday) || 0) + (dir === 'spark' ? 1 : 0),
        },
        recharge: rc
          ? { ...rc, like: dir === 'like' ? dec(rc.like) : rc.like, spark: dir === 'spark' ? dec(rc.spark) : rc.spark }
          : rc,
      };
    });
  }, []);
  return [sub, load, bumpUsage];
}

/* Derive the live allowance state from a subscription payload. Prefers the drip
   `recharge` buckets (balance + ms to the next +1 charge); falls back to the old
   usage math for a pre-drip API. Returns null when there's nothing to show.
   `*Next` is ms until the next charge (null = full / unlimited). */
function deriveLimits(sub) {
  const ent = sub?.entitlements;
  if (!ent) return null;
  const clamp = (n) => Math.max(0, Math.floor(Number(n) || 0));
  const unlimitedLikes = ent.likesPerDay === 'unlimited';

  const rc = sub?.recharge;
  if (rc) {
    // Drip buckets are the source of truth. rc.like === null → unlimited.
    const sparksLeft = rc.spark ? clamp(rc.spark.balance) : null;
    const sparkNext = rc.spark ? rc.spark.nextChargeMs : null;
    const likesLeft = rc.like ? clamp(rc.like.balance) : null;
    const likeNext = rc.like ? rc.like.nextChargeMs : null;
    if (sparksLeft == null && likesLeft == null && !unlimitedLikes) return null;
    return { sparksLeft, sparkNext, likesLeft, likeNext, unlimitedLikes };
  }

  // Legacy fallback (API without `recharge`): no live countdown.
  const usage = sub?.usage || {};
  const sparksLeft = typeof ent.superLikesPerDay === 'number' ? clamp(ent.superLikesPerDay - clamp(usage.sparkToday)) : null;
  const likesLeft = typeof ent.likesPerDay === 'number' ? clamp(ent.likesPerDay - clamp(usage.yesToday)) : null;
  if (sparksLeft == null && likesLeft == null && !unlimitedLikes) return null;
  return { sparksLeft, sparkNext: null, likesLeft, likeNext: null, unlimitedLikes };
}

/* A single mono allowance pill (icon + "N left"), on-design (accent hairline).
   `tone` shifts the accent so Sparks read love/pink and likes read accent.
   `recharging` (count == 0) dims it to a countdown chip and makes it tappable. */
function LimitPill({ icon, label, tone = 'accent', recharging = false, onClick }) {
  const base = tone === 'love' ? 'var(--love)' : 'var(--accent)';
  const c = recharging ? 'var(--text-3)' : base;
  return (
    <span className="flm-mono" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, flex: 'none',
      padding: '4px 9px', borderRadius: 99, fontSize: 9, color: c,
      cursor: onClick ? 'pointer' : 'default',
      border: '1px solid ' + (recharging ? 'var(--border-bright)' : tone === 'love' ? 'oklch(0.71 0.20 354 / 0.4)' : 'rgba(255,212,145,0.4)'),
      background: recharging ? 'var(--surface-2)' : tone === 'love' ? 'var(--love-tint)' : 'var(--accent-tint)',
    }}>
      <Icon name={icon} size={11} color={c} fill={recharging ? 'none' : c} />
      {recharging ? <Icon name="rewind" size={10} color={c} /> : null}
      {label}
    </span>
  );
}

/* The HUD row itself — renders nothing if there's no allowance to show. When a
   counter is spent (0), the pill becomes a LIVE "back in Xh Ym" countdown to the
   NEXT drip charge (not a midnight reset) and taps through to the upgrade flow. */
function LimitsHud({ sub, t, faNum, onUpgrade }) {
  // Tick so the countdown stays live without a heavy timer.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const lim = deriveLimits(sub);
  // Snapshot the next-charge instants when the payload changes, then count down to
  // them locally so the pill ticks between server fetches.
  const nextAt = React.useMemo(
    () => ({
      spark: lim?.sparkNext != null ? Date.now() + lim.sparkNext : null,
      like: lim?.likeNext != null ? Date.now() + lim.likeNext : null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseat on each payload
    [sub],
  );
  if (!lim) return null;
  const { sparksLeft, likesLeft, unlimitedLikes } = lim;

  const fmtIn = (at) => {
    if (at == null) return t('limitSoon');
    const totalMin = Math.max(0, Math.ceil((at - Date.now()) / 60000));
    if (totalMin <= 1) return t('limitSoon');
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${faNum(h)}h ${faNum(m)}m` : `${faNum(m)}m`;
  };
  const pill = (icon, tone, left, at, fullLabel) =>
    left === 0
      ? <LimitPill icon={icon} tone={tone} recharging onClick={onUpgrade} label={t('limitBackIn', { t: fmtIn(at) })} />
      : <LimitPill icon={icon} tone={tone} label={fullLabel} />;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 4px', flex: 'none', flexWrap: 'wrap' }}>
      {sparksLeft != null ? pill('zap', 'love', sparksLeft, nextAt.spark, t('sparksLeft', { n: faNum(sparksLeft) })) : null}
      {likesLeft != null ? (
        pill('heart', 'accent', likesLeft, nextAt.like, t('likesLeft', { n: faNum(likesLeft) }))
      ) : unlimitedLikes ? (
        <LimitPill icon="heart" tone="accent" label={t('likesUnlimited')} />
      ) : null}
    </div>
  );
}

/* Daily-claim card — a free spark per day with a growing streak. The #1 reason to
   open daily; on claim it tops up the spark bucket (onClaimed refreshes the HUD).
   Claimable → tappable accent card; claimed → muted "next in Xh Ym" countdown. */
function DailyClaim({ t, faNum, onClaimed }) {
  const [daily, setDaily] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (isDemo()) return;
    getDaily().then((r) => r && setDaily({ ...r, nextAt: Date.now() + (r.nextClaimInMs || 0) })).catch(() => {});
  }, []);
  React.useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  if (!daily) return null;

  const rewardLabel = (rw) => {
    if (!rw) return '';
    const parts = [];
    if (rw.sparks) parts.push(t('dailySparksReward', { n: faNum(rw.sparks) }));
    if (rw.likes) parts.push(t('dailyLikesReward', { n: faNum(rw.likes) }));
    return parts.join(' · ');
  };
  const fmtIn = (at) => {
    const totalMin = Math.max(0, Math.ceil((at - Date.now()) / 60000));
    if (totalMin <= 1) return t('limitSoon');
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${faNum(h)}h ${faNum(m)}m` : `${faNum(m)}m`;
  };
  const claim = () => {
    if (busy || !daily.claimable) return;
    setBusy(true);
    haptic('success');
    claimDaily()
      .then((r) => {
        if (r) {
          setDaily((d) => ({ ...d, claimable: false, streak: r.streak, nextClaimInMs: r.nextClaimInMs, nextAt: Date.now() + (r.nextClaimInMs || 0) }));
          if (r.ok) onClaimed?.();
        }
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const accent = daily.claimable;
  return (
    <div
      onClick={claim}
      style={{
        margin: '0 16px 6px', padding: '9px 13px', borderRadius: 14, flex: 'none',
        display: 'flex', alignItems: 'center', gap: 10, cursor: daily.claimable ? 'pointer' : 'default',
        border: '1px solid ' + (accent ? 'rgba(255,212,145,0.5)' : 'var(--border)'),
        background: accent ? 'var(--accent-tint)' : 'var(--surface-2)',
        transition: 'all 0.2s var(--ease-out-expo)',
      }}
      className={accent ? 'flm-press' : undefined}
    >
      <span style={{ fontSize: 17 }}>🔥</span>
      <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <b style={{ fontSize: 13, color: 'var(--text)' }}>
          {daily.streak > 0 ? t('dailyStreakN', { n: faNum(daily.streak) }) : t('dailyTitle')}
        </b>
        <span className="flm-mono" style={{ fontSize: 9, color: accent ? 'var(--accent)' : 'var(--text-3)' }}>
          {daily.claimable ? rewardLabel(daily.reward) : t('dailyNextIn', { t: fmtIn(daily.nextAt) })}
        </span>
      </span>
      {daily.claimable ? (
        <span className="flm-mono" style={{
          fontSize: 10, color: '#000', background: 'var(--accent)', padding: '5px 12px', borderRadius: 99,
          opacity: busy ? 0.6 : 1,
        }}>{busy ? t('loadingLabel') : t('dailyClaim')}</span>
      ) : (
        <Icon name="check" size={15} color="var(--accent)" />
      )}
    </div>
  );
}

/* Countdown to tonight's 20:00 Salon reset. */
function useSalonCountdown() {
  const calc = () => {
    const now = new Date();
    const target = new Date(now);
    target.setHours(20, 0, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    let s = Math.max(0, Math.floor((target - now) / 1000));
    const hh = String(Math.floor(s / 3600)).padStart(2, '0');
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return hh + ':' + mm + ':' + ss;
  };
  const [left, setLeft] = React.useState(calc);
  React.useEffect(() => {
    const id = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return left;
}

/* Facts grid for the optional dating attributes — only fields that are SET
   render (no empty rows). Labels in mono, values clean (the design language).
   `full` is the public profile from GET /v1/profiles/:userId. */
function AttrFacts({ full, t, faNum }) {
  if (!full) return null;
  const rows = [];
  const push = (label, value) => { if (value != null && value !== '') rows.push([label, value]); };
  if (full.heightCm) push(t('attrHeight'), `${faNum(full.heightCm)} ${t('attrUnitCm')}`);
  if (full.weightKg) push(t('attrWeight'), `${faNum(full.weightKg)} ${t('attrUnitKg')}`);
  push(t('attrBodyType'), attrValueLabel(t, 'bodyType', full.bodyType));
  push(t('attrRelationshipGoal'), attrValueLabel(t, 'relationshipGoal', full.relationshipGoal));
  push(t('attrWantsChildren'), attrValueLabel(t, 'wantsChildren', full.wantsChildren));
  push(t('attrSmoking'), attrValueLabel(t, 'smoking', full.smoking));
  push(t('attrDrinking'), attrValueLabel(t, 'drinking', full.drinking));
  push(t('attrExercise'), attrValueLabel(t, 'exercise', full.exercise));
  push(t('attrDiet'), attrValueLabel(t, 'diet', full.diet));
  push(t('attrPets'), attrValueLabel(t, 'pets', full.pets));
  push(t('attrEducation'), attrValueLabel(t, 'education', full.education));
  push(t('attrZodiac'), attrValueLabel(t, 'zodiac', full.zodiac));
  if (full.religion) push(t('attrReligion'), full.religion);
  if (Array.isArray(full.languages) && full.languages.length) push(t('attrLanguages'), full.languages.join(' · '));
  if (!rows.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span className="flm-label">{t('attrFacts')}</span>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '11px 13px', background: 'var(--surface)' }}>
            <span className="flm-mono" style={{ fontSize: 8.5, color: 'var(--text-3)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)' }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Lightbox — full-size photo viewer over a dim backdrop ---------- */
/* Reuses FullOverlay (highest z so it sits above the detail sheet). `photos` is
   the same absolute-URL array the hero pager + gallery strip use; `index`/`setIndex`
   share the pager's position so opening from a thumbnail lands on that exact photo
   and closing returns the hero to it. Left/right arrows + horizontal swipe page
   through; tapping the dim backdrop (or the ✕) closes. Lightweight — no deps. */
function Lightbox({ photos, index, setIndex, t, faNum, onClose }) {
  const total = photos.length;
  const safe = Math.min(Math.max(0, index), Math.max(0, total - 1));
  const go = (n) => setIndex(Math.min(total - 1, Math.max(0, n)));
  /* swipe: track the down-x on the image; a decisive horizontal release pages.
     The image stops propagation so a swipe never closes via the backdrop. */
  const startX = React.useRef(null);
  const onDown = (e) => { startX.current = e.clientX; };
  const onUp = (e) => {
    if (startX.current == null) return;
    const dx = e.clientX - startX.current;
    startX.current = null;
    if (dx > 48) go(safe - 1);
    else if (dx < -48) go(safe + 1);
  };
  return (
    <FullOverlay open={true} z={80} bg="rgba(0,0,0,0.94)">
      {/* close */}
      <button className="flm-ghost-btn" onClick={onClose} aria-label={t('closePhoto')} style={{
        position: 'absolute', top: 'max(18px, var(--safe-top))', insetInlineEnd: 16, zIndex: 6,
        color: '#fff', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: 38, height: 38,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      }}>
        <Icon name="x" size={20} />
      </button>
      {/* counter */}
      {total > 1 ? (
        <span className="flm-glass" style={{
          position: 'absolute', top: 'max(20px, var(--safe-top))', insetInlineStart: 16, zIndex: 6,
          padding: '5px 11px', borderRadius: 99,
        }}>
          <span className="flm-mono" style={{ fontSize: 10, color: '#fff' }}>{faNum(safe + 1)} / {faNum(total)}</span>
        </span>
      ) : null}

      {/* the photo, contained on the dim backdrop. The backdrop click closes;
          the image stops that so a swipe never closes mid-gesture. */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <img
          src={photos[safe]}
          alt=""
          onPointerDown={onDown}
          onPointerUp={onUp}
          onClick={(e) => e.stopPropagation()}
          draggable={false}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', touchAction: 'pan-y', userSelect: 'none' }}
        />
      </div>

      {/* page arrows + dots */}
      {total > 1 ? (
        <React.Fragment>
          <button className="flm-ghost-btn" onClick={() => go(safe - 1)} aria-label={t('prevPhoto')} disabled={safe === 0} style={{
            position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5,
            width: 40, height: 40, borderRadius: '50%', color: '#fff', opacity: safe === 0 ? 0.3 : 1,
            background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          }}>
            <Icon name="chevLeft" size={20} />
          </button>
          <button className="flm-ghost-btn" onClick={() => go(safe + 1)} aria-label={t('nextPhoto')} disabled={safe === total - 1} style={{
            position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5,
            width: 40, height: 40, borderRadius: '50%', color: '#fff', opacity: safe === total - 1 ? 0.3 : 1,
            background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          }}>
            <Icon name="chevRight" size={20} />
          </button>
          <span style={{ position: 'absolute', bottom: 'max(22px, var(--safe-bottom))', insetInline: 0, display: 'flex', justifyContent: 'center', gap: 6, zIndex: 5 }}>
            {photos.map((_, i) => (
              <i key={i} onClick={() => go(i)} style={{
                width: i === safe ? 18 : 6, height: 6, borderRadius: 99, cursor: 'pointer',
                background: i === safe ? 'var(--accent)' : 'rgba(255,255,255,0.4)', transition: 'all 0.25s var(--ease-out-expo)',
              }}></i>
            ))}
          </span>
        </React.Fragment>
      ) : null}
    </FullOverlay>
  );
}

/* ---------- Profile detail overlay — "Viewing Sasha" artboard ---------- */
/* `actions` (default true) shows the floating pass/like/spark/gift row + the gift
   hint. Pass actions={false} for a READ-ONLY view (e.g. opened from a chat thread
   header where the two are already matched) — every other section still renders. */
function ProfileDetail({ profile, t, faNum, onClose, onPass, onLike, onSpark, onUpgrade, canMessage = false, actions = true, likesExhausted = false, sparksExhausted = false }) {
  const [playing, setPlaying] = React.useState(false);
  const [guess, setGuess] = React.useState(null);
  const [shot, setShot] = React.useState(0); // active photo index — segments + tap zones + gallery
  const [lightbox, setLightbox] = React.useState(false); // full-size photo viewer open?
  /* Action close transition (owner: "now it shows nothing, suddenly next card").
     Tapping Pass/Like/Spark first SHRINKS this overlay toward the deck (scale-down
     + fade, ~200ms) and only THEN hands off to the parent, which replays the deck's
     own animated fling for that card. `closing` drives the transform; a single timer
     fires the handoff after the shrink and is cleared on unmount. */
  const ACT_CLOSE_MS = 200;
  const [closing, setClosing] = React.useState(false);
  const actTimer = React.useRef(null);
  React.useEffect(() => () => { if (actTimer.current) clearTimeout(actTimer.current); }, []);
  React.useEffect(() => { setClosing(false); }, [profile]);
  /* Infinity "Message before match" — the compose Sheet + send/403/409 handling
     live in the shared useMessageCompose controller (same flow the deck row uses).
     `compose.open(profile)` opens it; `compose.sheet` is rendered below. */
  const compose = useMessageCompose({ t, onUpgrade });
  const openCompose = compose.open;
  /* Gift affordance lives here (moved off the deck row). Gifts are sent from the
     chat after matching (collectible TON gifts mint to their wallet), so the
     button surfaces a transient hint rather than starting a flow this view can't
     own — no new wiring, honest about where gifting happens. */
  const [giftNote, setGiftNote] = React.useState(false);
  React.useEffect(() => setGiftNote(false), [profile]);
  /* Safety actions (block/report) open IN-APP sheets — window.confirm is blocked
     inside Telegram WebViews, which is why the old confirm-then-act flow did
     nothing. null | 'block' | 'report'. */
  const [safety, setSafety] = React.useState(null);
  React.useEffect(() => setSafety(null), [profile]);
  /* lazy-loaded full public profile (attributes + coarse place) — fetched when a
     LIVE card opens. Mock/demo cards (no userId) keep the artboard garnish. */
  const [full, setFull] = React.useState(null);
  React.useEffect(() => {
    setFull(null);
    if (!profile?.userId) return;
    let alive = true;
    getProfile(profile.userId).then((res) => { if (alive) setFull(res); }).catch(() => {});
    return () => { alive = false; };
  }, [profile?.userId]);
  React.useEffect(() => { setShot(0); setLightbox(false); }, [profile]);
  /* real audio when the card OR the lazy-loaded full profile carries a voice intro
     (full.voiceUrl is API-relative → mediaUrl). Lets sparse entry points (map zone,
     chat header) play the voice once `full` lands, same as the deck. */
  const voiceUrl = (profile && profile.voiceUrl) || (full && full.voiceUrl ? mediaUrl(full.voiceUrl) : null);
  const audio = React.useRef(null);
  React.useEffect(() => () => {
    if (audio.current) { audio.current.pause(); audio.current = null; }
  }, [voiceUrl]);
  const togglePlay = () => {
    if (!voiceUrl) { setPlaying((p) => !p); return; }
    if (playing) { if (audio.current) audio.current.pause(); setPlaying(false); return; }
    if (!audio.current) {
      const a = new Audio(voiceUrl);
      a.onended = () => setPlaying(false);
      a.onerror = () => setPlaying(false);
      audio.current = a;
    }
    audio.current.play().catch(() => setPlaying(false));
    setPlaying(true);
  };
  /* Routed Pass/Like/Spark from the action row: shrink the overlay toward the deck,
     then hand the decision to the parent (which replays the deck fling for the top
     card). Guarded so a double-tap during the close can't fire twice. The parent
     owns closing the overlay + recording the swipe — we never record here, so the
     deck's fling stays the single source of the swipe (no double-record). */
  const act = (dir, fn) => {
    if (closing || !fn) return;
    // Limit gate (when the caller supplies exhausted flags, e.g. the map overlay):
    // a spent like/spark goes STRAIGHT to the upgrade flow — no shrink, no swipe —
    // mirroring the deck's flingGated. Pass is never gated.
    if (dir === 'like' && likesExhausted) { haptic('light'); onUpgrade && onUpgrade(); return; }
    if (dir === 'spark' && sparksExhausted) { haptic('light'); onUpgrade && onUpgrade(); return; }
    // A light tap acknowledges the press; the directional/celebratory haptic is the
    // deck fling's job (~ACT_CLOSE_MS later) so a spark never double-buzzes.
    haptic('light');
    setClosing(true);
    actTimer.current = setTimeout(() => { fn(profile); }, ACT_CLOSE_MS);
  };
  if (!profile) return null;
  const pct = matchPct(profile);
  /* live card = real person: only verified facts render (no mock prompt/gift garnish) */
  const live = Boolean(profile.userId);
  const photos = Array.isArray(profile.photos) ? profile.photos : [];
  /* Unified photo source for the hero pager + gallery strip + lightbox. The
     lazy-loaded full profile carries PhotoItem[] (url is API-relative + isCover),
     the richest source once it lands — pass each url through mediaUrl() and remember
     which one is the cover. Before it loads (and for mock cards) fall back to the
     card's already-absolute photos[] (cover = first). */
  const fullPhotos = live && full && Array.isArray(full.photos) ? full.photos : null;
  // Register each full-profile photo's blur-up placeholder (url -> hash).
  if (fullPhotos) fullPhotos.forEach((p) => registerBlur(mediaUrl(p.url), p.blurHash));
  const gallery = fullPhotos && fullPhotos.length
    ? fullPhotos.map((p) => mediaUrl(p.url)).filter(Boolean)
    : photos.length ? photos : profile.photoUrl ? [profile.photoUrl] : [];
  const coverIdx = fullPhotos && fullPhotos.length
    ? Math.max(0, fullPhotos.findIndex((p) => p.isCover))
    : 0;
  const shotSafe = Math.min(shot, Math.max(0, gallery.length - 1));
  /* Merge the lazy-loaded full profile over the (possibly sparse) card so EVERY
     entry point — deck, MAP zone, chat header — shows the same rich detail once
     `full` lands. nearbyToCard (map) carries no age/bio/job/voice/place; full fills
     them, so a profile opened from the map looks identical to one from the deck. */
  const dispAge = profile.age !== '' && profile.age != null ? profile.age : (full && full.age != null ? full.age : '');
  const dispBio = (full && full.bio) || profile.bio || '';
  const dispJob = profile.job || (full && full.job) || '';
  const dispVerified = Boolean(profile.verified) || Boolean(full && full.isVerified);
  /* coarse place from the full profile when the card has none (never coordinates). */
  const fullPlace = !profile.city && !profile.country && full && (full.city || full.country)
    ? [full.city, countryName(full.country)].filter(Boolean).join(', ')
    : '';
  /* safety: live cards (have userId) hit the real API via the in-app sheets below;
     mock/demo cards (no userId) open the same UI but submit is a no-op. */
  const confirmBlock = () => {
    if (!profile.userId) { setSafety(null); onClose(); return; }
    return blockUser(profile.userId)
      .then(() => { setSafety(null); onClose(); })
      .catch(() => setSafety(null));
  };
  const submitReport = (reason, detail) =>
    profile.userId ? reportUser(profile.userId, reason, detail) : Promise.resolve();
  /* Shrink-toward-deck transition shared by the scroll body + the floating action
     row: scale down to 0.92 + fade as the overlay hands off to the deck fling.
     transform-origin bottom-center so it collapses DOWN toward the deck below. */
  const closeTransform = {
    transform: closing ? 'scale(0.92)' : 'scale(1)',
    opacity: closing ? 0 : 1,
    transformOrigin: 'bottom center',
    transition: `transform ${ACT_CLOSE_MS}ms var(--ease-out-expo), opacity ${ACT_CLOSE_MS}ms var(--ease-out-expo)`,
  };
  return (
    <FullOverlay open={true} z={65}>
      <div className="flm-scroll" data-screen-label="Profile detail" style={closeTransform}>
        {/* hero */}
        <div style={{ position: 'relative', height: 360, flex: 'none' }}>
          <GradPhoto profile={profile} photo={gallery[shotSafe] ?? profile.photoUrl} w={1024} initialSize="9rem" style={{ position: 'absolute', inset: 0 }}>
            <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.3), transparent 30%, transparent 55%, #000 100%)', zIndex: 2 }}></span>
            <PhotoSegments total={live ? gallery.length : 4} active={live ? shotSafe : 1} />
            {/* stories tap zones: left third = previous photo, right third = next */}
            {live && gallery.length > 1 ? (
              <React.Fragment>
                <span onClick={() => setShot(Math.max(0, shotSafe - 1))} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '33%', zIndex: 3, cursor: 'pointer' }}></span>
                {/* middle third taps into the full-size lightbox (at the current photo) */}
                <span onClick={() => setLightbox(true)} style={{ position: 'absolute', left: '33%', top: 0, bottom: 0, width: '34%', zIndex: 3, cursor: 'zoom-in' }}></span>
                <span onClick={() => setShot(Math.min(gallery.length - 1, shotSafe + 1))} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '33%', zIndex: 3, cursor: 'pointer' }}></span>
              </React.Fragment>
            ) : live && gallery.length === 1 ? (
              <span onClick={() => setLightbox(true)} style={{ position: 'absolute', inset: 0, zIndex: 1, cursor: 'zoom-in' }}></span>
            ) : null}
            <button className="flm-ghost-btn" onClick={onClose} style={{ position: 'absolute', top: 26, insetInlineStart: 14, zIndex: 4, color: '#fff', background: 'rgba(0,0,0,0.4)', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
              <Icon name="chevDown" size={20} />
            </button>
            <div style={{ position: 'absolute', left: 18, right: 18, bottom: 14, zIndex: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="flm-display" style={{ fontSize: 32, color: '#fff' }}>{profile.name}{dispAge !== '' && dispAge != null ? `, ${faNum(dispAge)}` : ''}</span>
                {dispVerified ? <Verif size={19} /> : null}
                {profile.isPremium ? <PremiumBadge t={t} /> : null}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3, color: 'var(--text-2)', fontSize: 13.5 }}>
                <Icon name="mapPin" size={13} />
                <span>{[fullPlace, locationLine(profile, t, faNum)].filter(Boolean).join(' · ')}{dispJob ? ' · ' + dispJob : ''}</span>
              </div>
            </div>
            <span className="flm-glass" style={{ position: 'absolute', insetInlineEnd: 14, bottom: 16, zIndex: 3, padding: '7px 12px', borderRadius: 99, display: 'flex', gap: 7, alignItems: 'center' }}>
              <span className="flm-dot" style={{ width: 6, height: 6 }}></span>
              <span className="flm-mono" style={{ fontSize: 9, color: '#fff' }}>{t('matchPct', { n: faNum(pct) })}</span>
            </span>
          </GradPhoto>
        </div>

        <div style={{ padding: '14px 18px 130px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* voice intro — live cards only render a REAL recording */}
          {!live || voiceUrl ? (
            <div className="flm-hairline" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 13, padding: '13px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16 }}>
              <button className="flm-ghost-btn" onClick={togglePlay} style={{ width: 42, height: 42, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', padding: 0, boxShadow: '0 0 18px rgba(255,212,145,0.35)', cursor: 'pointer' }}>
                <Icon name={playing ? 'pause' : 'play'} size={17} color="#000" fill="#000" />
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 4, minWidth: 0 }}>
                <span className="flm-label">{t('voiceIntro')}</span>
                <VoiceWave playing={playing} bars={30} height={20} />
              </div>
              <span className="flm-mono" style={{ fontSize: 10 }}>{faNum(fmtVoice(profile.voice || (full && full.voiceDuration) || 0))}</span>
            </div>
          ) : null}

          {/* about */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="flm-label">{t('about')}</span>
            <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 15.5, lineHeight: 1.55, color: 'var(--text-2)' }}>{dispBio}</p>
          </div>

          {/* optional dating attributes — only the fields they've filled */}
          <AttrFacts full={full} t={t} faNum={faNum} />

          {/* prompt card — two truths & a lie (demo garnish; real prompts aren't live yet) */}
          {!live ? (
            <div className="flm-hairline" style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 7, padding: 16, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16 }}>
              <span className="flm-mono" style={{ fontSize: 9, color: 'var(--love)' }}>{t('detailPrompt')}</span>
              <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.5, color: 'var(--text)' }}>{t('promptQuote')}</p>
              <div style={{ display: 'flex', gap: 6 }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} onClick={() => setGuess(i)} className={'flm-chip' + (guess === i ? ' is-love' : '')} style={{ fontSize: 12.5, padding: '5px 12px', cursor: 'pointer' }}>{faNum(i + 1)}</span>
                ))}
              </div>
            </div>
          ) : null}

          {/* tastes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="flm-label">{t('tastesShared', { n: faNum(profile.common) })}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {profile.interests.map((tag, idx) => (
                <span key={tag} className={'flm-chip' + (idx < profile.common ? ' is-on' : '')} style={{ fontSize: 12.5 }}>
                  {idx < profile.common ? <Icon name="check" size={10} strokeWidth={3} /> : null}{tag}
                </span>
              ))}
            </div>
          </div>

          {/* second photo + on-chain gifts — demo keeps the artboard garnish;
              live cards show their REAL second photo only (no fake gift counts) */}
          {!live ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 10 }}>
              <div style={{ height: 150, borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
                <GradPhoto profile={{ name: profile.name, grad: [profile.grad[1], profile.grad[0]] }} initialSize="4rem" style={{ position: 'absolute', inset: 0 }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center', padding: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16 }}>
                <span className="flm-label">{t('giftsReceived')}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon name="rose" size={22} color="var(--love)" />
                  <Icon name="gem" size={22} color="var(--ton)" />
                  <span className="flm-mono" style={{ fontSize: 10 }}>×{faNum(12)}</span>
                </div>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-3)' }}>{t('giftedTop')}</span>
              </div>
            </div>
          ) : gallery.length ? (
            /* Photos gallery — ALL of their photos as a horizontal thumbnail strip.
               Tapping any tile jumps the hero to it AND opens the full-size lightbox;
               the cover wears an accent ring + label, the photo on the hero a subtle
               brighter border so the strip mirrors what's up top. Same gallery[] the
               hero pager + lightbox share (full.photos via mediaUrl, else card photos). */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="flm-label">{t('photos')}</span>
              {/* onWheel maps a vertical wheel to horizontal scroll so the strip is
                  reachable on desktop too (touch already drag-scrolls it). */}
              <div
                className="flm-hscroll"
                style={{ gap: 8, padding: '2px 0' }}
                onWheel={(e) => {
                  if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && e.currentTarget.scrollWidth > e.currentTarget.clientWidth) {
                    e.currentTarget.scrollLeft += e.deltaY;
                  }
                }}
              >
                {gallery.map((src, i) => {
                  const isCover = i === coverIdx;
                  const isActive = i === shotSafe;
                  return (
                    <button
                      key={src + i}
                      onClick={() => { setShot(i); setLightbox(true); }}
                      className="flm-ghost-btn"
                      aria-label={isCover ? t('photoCover') : `${t('photos')} ${faNum(i + 1)}`}
                      style={{
                        position: 'relative', flex: 'none', width: 78, height: 78, padding: 0, borderRadius: 14,
                        overflow: 'hidden', cursor: 'zoom-in', background: 'var(--surface)',
                        border: '1px solid ' + (isCover ? 'rgba(255,212,145,0.55)' : isActive ? 'var(--border-bright)' : 'var(--border)'),
                        boxShadow: isCover ? '0 0 0 1px rgba(255,212,145,0.25)' : 'none',
                      }}
                    >
                      <GradPhoto profile={profile} photo={src} w={256} initialSize="2.4rem" style={{ position: 'absolute', inset: 0 }} />
                      {isCover ? (
                        <span className="flm-mono" style={{
                          position: 'absolute', left: 5, bottom: 5, zIndex: 2, padding: '2px 6px', borderRadius: 99,
                          fontSize: 7.5, color: 'var(--accent)', background: 'rgba(0,0,0,0.6)',
                          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                        }}>{t('photoCover')}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* block · report (Share profile removed) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, padding: '4px 0 12px' }}>
            <span onClick={() => setSafety('block')} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)', cursor: 'pointer' }}>{t('block')}</span>
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--border-bright)' }}></span>
            <span onClick={() => setSafety('report')} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)', cursor: 'pointer' }}>{t('report')}</span>
          </div>
        </div>
      </div>

      {/* transient gift hint — gifting happens in chat after a match.
          Hidden this round: gifts are off app-wide (GIFTS_ENABLED). */}
      {GIFTS_ENABLED && actions && giftNote ? (
        <div style={{
          position: 'absolute', bottom: 84, insetInline: 0, display: 'flex', justifyContent: 'center', zIndex: 11,
          animation: 'flmFadeIn 0.25s var(--ease-out-expo)', pointerEvents: 'none',
        }}>
          <span className="flm-glass" style={{ padding: '8px 14px', borderRadius: 99, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="gift" size={14} color="var(--ton)" />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12.5, color: 'var(--text)' }}>{t('giftAfterMatch')}</span>
          </span>
        </div>
      ) : null}

      {/* floating actions: pass · like · spark (· gift) · message(premium). Like stays
          the emphasized center action; Spark is the super-like (mirrors the deck). The
          premium Message (gold + crown) is the pre-match reach-out — shown to everyone;
          non-entitled taps route to onUpgrade via the shared compose flow (the 403 path),
          so it doubles as an upsell. Gift is hidden this round (GIFTS_ENABLED off) — the
          button code survives behind the flag. Hidden entirely when actions={false}
          (read-only view from a chat — already matched, so no message-before-match). */}
      {actions ? (
        <div style={{ position: 'absolute', bottom: 16, insetInline: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, zIndex: 10, ...closeTransform }}>
          {/* Pass/Like/Spark route through act() → shrink THEN the parent replays the
              deck's animated fling for this card (it records the swipe; we never do,
              so no double-record). */}
          <ActionBtn icon="x" size={52} iconSize={20} color="var(--text-2)" ring="var(--text-2)" bg="rgba(10,10,10,0.9)" onClick={() => act('pass', onPass)} />
          <ActionBtn icon="heart" size={62} iconSize={25} color="#000" bg="var(--accent)" border="none" ring="var(--accent)" glow fill onClick={() => act('like', onLike)} />
          <ActionBtn icon="zap" size={52} iconSize={20} color="var(--love)" ring="var(--love)" bg="var(--love-tint)" border="1px solid oklch(0.71 0.20 354 / 0.55)" fill onClick={() => act('spark', onSpark)} />
          {GIFTS_ENABLED ? (
            <ActionBtn icon="gift" size={52} iconSize={20} color="var(--ton)" ring="var(--ton)" bg="rgba(10,10,10,0.9)" onClick={() => { setGiftNote(true); setTimeout(() => setGiftNote(false), 2400); }} />
          ) : null}
          {/* premium Message-before-match — live cards only (needs a userId to reach).
              canMessage opens the compose directly; non-entitled taps go straight to
              the upgrade flow (upsell). Crown shown only when LOCKED (canMessage false).
              The shared sheet's 403 path is the backstop. */}
          {profile.userId ? (
            <PremiumMessageBtn t={t} size={52} iconSize={20} locked={!canMessage} onClick={() => (canMessage ? openCompose(profile) : onUpgrade && onUpgrade('infinity'))} />
          ) : null}
        </div>
      ) : null}

      {/* Message-before-match compose sheet (shared controller) — small textarea +
          Send. Success → "Request sent" → auto-close; 403 → onUpgrade; 409/coded →
          inline "already connected/requested" note. */}
      {compose.sheet}

      {/* Safety: in-app block confirm + report wizard (replace window.confirm,
          which Telegram WebViews block). Reporting also blocks server-side. */}
      <ConfirmSheet
        open={safety === 'block'}
        t={t}
        title={t('blockTitle')}
        body={t('blockBody')}
        confirmLabel={t('block')}
        onConfirm={confirmBlock}
        onClose={() => setSafety(null)}
      />
      <ReportSheet
        open={safety === 'report'}
        name={profile.name}
        t={t}
        onSubmit={submitReport}
        onClose={() => setSafety(null)}
        onDone={() => { setSafety(null); onClose(); }}
      />

      {/* full-size lightbox — opened by tapping the hero or any gallery thumbnail.
          Shares `shot` so it lands on the current photo and the hero follows it
          on close. Sits above the detail sheet AND the action row (z=80). */}
      {lightbox && gallery.length ? (
        <Lightbox photos={gallery} index={shotSafe} setIndex={setShot} t={t} faNum={faNum} onClose={() => setLightbox(false)} />
      ) : null}
    </FullOverlay>
  );
}

/* ---------- Match overlay — "Match moment · icebreaker" artboard ---------- */
function MatchOverlay({ profile, meName, mePhoto, t, onClose, onMessage }) {
  const [guess, setGuess] = React.useState(2);
  /* REAL icebreaker for live matches: the match's interests as conversation
     starters, shared ones (we both picked) highlighted first. Fetches their full
     profile + mine so it works from the deck AND the map (sparse) cards. */
  const [ice, setIce] = React.useState(null);
  React.useEffect(() => {
    if (!profile?.userId) { setIce(null); return; }
    let alive = true;
    Promise.all([getProfile(profile.userId), getMe().catch(() => null)])
      .then(([full, me]) => {
        if (!alive) return;
        const mine = new Set((me?.profile?.interests || []).map((i) => i.slug));
        const theirs = Array.isArray(full?.interests) ? full.interests : [];
        const list = theirs
          .map((i) => ({ slug: i.slug, label: t(i.labelKey) || i.slug, shared: mine.has(i.slug) }))
          .sort((a, b) => (b.shared ? 1 : 0) - (a.shared ? 1 : 0));
        setIce({ list, sharedCount: list.filter((x) => x.shared).length });
      })
      .catch(() => { if (alive) setIce(null); });
    return () => { alive = false; };
  }, [profile?.userId, t]);
  if (!profile) return null;
  const me = { name: meName || t('you'), grad: ['#4B006E', '#1a0026'], online: false };
  return (
    <FullOverlay open={true} z={90} bg="#000">
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <GlowBlob color="is-love" size={300} x="55%" y="32%" o={0.8} />
        <GlowBlob color="is-purple" size={260} x="85%" y="80%" />
      </div>
      <button className="flm-ghost-btn" onClick={onClose} style={{ position: 'absolute', top: 'max(14px, var(--safe-top))', insetInlineEnd: 14, zIndex: 5, color: 'var(--text-muted)' }}>
        <Icon name="x" size={20} />
      </button>

      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 26px', zIndex: 2, textAlign: 'center' }} data-screen-label="Match">
        <span className="flm-mono" style={{ color: 'var(--love)', letterSpacing: '0.3em' }}>{t('mutual')}</span>
        <div className="flm-display" style={{ fontSize: 46, color: 'var(--text)', margin: '10px 0 30px' }}>
          {t('you')} +<br />{profile.name}
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: 156, marginBottom: 34, animation: 'flmMatchPop 0.6s var(--ease-spring)' }}>
          <GradPhoto profile={me} photo={mePhoto} initialSize="50px" style={{ width: 140, height: 140, borderRadius: 42, border: '2px solid var(--accent)', boxShadow: 'var(--glow-accent)', transform: 'rotate(-7deg) translateX(15px)' }} />
          <GradPhoto profile={profile} photo={profile.photoUrl} initialSize="50px" style={{ width: 140, height: 140, borderRadius: 42, border: '2px solid var(--love)', boxShadow: 'var(--glow-love)', transform: 'rotate(7deg) translateX(-15px)' }} />
          <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 44, height: 44, borderRadius: '50%', background: '#000', border: '1px solid var(--border-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <Icon name="heart" size={19} color="var(--love)" fill="var(--love)" />
          </span>
        </div>

        {/* icebreaker. LIVE match → their REAL interests as starters (shared first,
            highlighted). DEMO card (no userId) → the artboard guess-the-lie garnish. */}
        {profile.userId ? (
          ice && ice.list.length ? (
            <div className="flm-glass" style={{ width: '100%', padding: 16, display: 'flex', flexDirection: 'column', gap: 9, textAlign: 'start' }}>
              <span className="flm-mono" style={{ fontSize: 9, color: 'var(--love)' }}>
                {ice.sharedCount ? t('matchSharedTastes') : t('matchBreakIce')}
              </span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ice.list.slice(0, 8).map((i) => (
                  <span key={i.slug} className={'flm-chip' + (i.shared ? ' is-on' : '')} style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {i.shared ? <Icon name="check" size={10} strokeWidth={3} /> : null}{i.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null
        ) : (
          <div className="flm-glass" style={{ width: '100%', padding: 16, display: 'flex', flexDirection: 'column', gap: 9, textAlign: 'start' }}>
            <span className="flm-mono" style={{ fontSize: 9, color: 'var(--love)' }}>{t('matchIcebreaker')}</span>
            <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 14.5, lineHeight: 1.5, color: 'var(--text)' }}>{t('promptQuote')}</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[t('promptOpt1'), t('promptOpt2'), t('promptOpt3')].map((o, idx) => (
                <span key={o} onClick={() => setGuess(idx)} className={'flm-chip' + (guess === idx ? ' is-love' : '')} style={{ fontSize: 12, cursor: 'pointer' }}>{o}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 10, padding: '0 20px 24px', zIndex: 2 }}>
        <button className="flm-cta" onClick={onMessage}>{t('sayHiFirst')}</button>
        {/* gifting is off app-wide (GIFTS_ENABLED) — no "Send a rose" on a match */}
        {GIFTS_ENABLED ? (
          <button className="flm-cta is-ghost" onClick={onMessage} style={{ gap: 8 }}>
            <Icon name="gem" size={16} color="var(--ton)" />
            {t('sendRose')}
          </button>
        ) : null}
      </div>
    </FullOverlay>
  );
}

/* ---------- A · Classic swipe deck ---------- */
/* Entitlement-driven gating (owner testing):
   - `canRewind` / `canMessage` decide whether the Rewind / Message buttons wear a
     gold crown (LOCKED) — when the viewer's plan already grants the feature there's
     no crown and the action works normally. A locked tap routes to onUpgrade.
   - `likesExhausted` / `sparksExhausted` flag a FREE viewer who has spent today's
     allowance: tapping Like / Spark then opens the upgrade flow immediately instead
     of firing a swipe that 403s silently. Premium (unlimited) never exhausts. */
function SwipeDeck({ queue, t, faNum, onSwipe, onReset, onOpen, onRewind, onMessage, rewindNote, live, onUpgrade, canRewind = false, canMessage = false, likesExhausted = false, sparksExhausted = false, onOutOfLimit, flingRef }) {
  const [drag, setDrag] = React.useState({ dx: 0, dy: 0, on: false });
  const [leaving, setLeaving] = React.useState(null); // {id, dir}
  const [holding, setHolding] = React.useState(null); // {id} — spark burst playing on the STILL card (pre-fly)
  const [burst, setBurst] = React.useState(0); // bump → remount the spark SparkBurst
  const start = React.useRef(null);
  const top = queue[0];
  /* photo pager for the top card — side-third taps page, segments mirror it */
  const [shot, setShot] = React.useState(0);
  const topId = top ? top.id : null;
  React.useEffect(() => setShot(0), [topId]);
  const topPhotos = top && Array.isArray(top.photos) && top.photos.length ? top.photos : top && top.photoUrl ? [top.photoUrl] : [];

  /* Pending timeouts for the spark hold→fly→commit chain. Tracked so an unmount
     mid-sequence clears them — a spark in flight must never fire onSwipe twice or
     leak a timer (owner testing). Plain like/pass use a single short exit timer. */
  const timers = React.useRef([]);
  React.useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current = []; }, []);

  /* Spark hold — how long the SparkBurst plays CENTERED on the STILL (un-translated)
     top card before it lifts off. Owner: "show the animation [briefly] and THEN move
     the card" — so the burst reads as "spark!" on a stationary card, then the card
     flies. Tuned to ~260ms (sits in the 220-300ms sweet spot). */
  const SPARK_HOLD_MS = 260;
  /* Exit timing — the card stays MOUNTED and visibly animates its fly-out for this
     long BEFORE the queue advances + the swipe is recorded. Spark runs longer so its
     up-fly + the SparkBurst tail can breathe together. */
  const EXIT_MS = { spark: 560, like: 360, pass: 360 };
  const fling = (dir) => {
    // Single-fire guard: a spark mid-hold (`holding`) OR any card mid-exit
    // (`leaving`) blocks a second tap/swipe — no double-swipe, no double commit.
    if (!top || leaving || holding) return;
    // Haptics: Spark is the super-like — a strong, celebratory buzz; like/pass a
    // lighter tap. Guarded in lib/telegram (no-op off-platform).
    haptic(dir === 'spark' ? 'spark' : dir === 'like' ? 'medium' : 'light');
    const card = top;
    // Commit = advance the queue + record the swipe. Runs once, AFTER the exit fly.
    const commit = () => {
      setLeaving(null);
      setDrag({ dx: 0, dy: 0, on: false });
      setBurst(0);
      onSwipe(dir, card);
    };
    if (dir === 'spark') {
      // PHASE 1 — HOLD: fire the burst over the STILL card (no transform yet). The
      // card stays stationary (a subtle pulse only) so the SparkBurst plays clearly
      // centered on it for SPARK_HOLD_MS.
      setBurst((n) => n + 1);
      setHolding({ id: card.id });
      timers.current.push(setTimeout(() => {
        // PHASE 2 — FLY: clear the hold and enter the exit state so the card lifts
        // off (the existing spark up-fly transform) while the burst tail finishes.
        setHolding(null);
        setLeaving({ id: card.id, dir });
        // PHASE 3 — COMMIT: after the fly completes, advance + record.
        timers.current.push(setTimeout(commit, EXIT_MS.spark));
      }, SPARK_HOLD_MS));
      return;
    }
    // Like / Pass — unchanged: fly immediately (no hold), commit after the exit.
    setLeaving({ id: card.id, dir });
    timers.current.push(setTimeout(commit, EXIT_MS[dir] ?? 360));
  };

  /* Gated fling — the single entry point for Like/Spark taps AND right-swipe
     releases. A FREE viewer who has spent today's allowance never fires a swipe
     that would 403 silently; instead we open the upgrade flow (owner ask). Pass is
     never gated. Premium (unlimited) passes the flags as false so it always flings. */
  const flingGated = (dir) => {
    if (dir === 'like' && likesExhausted) { haptic('light'); onOutOfLimit && onOutOfLimit('likes'); return; }
    if (dir === 'spark' && sparksExhausted) { haptic('light'); onOutOfLimit && onOutOfLimit('sparks'); return; }
    fling(dir);
  };

  /* Expose the gated fling to the parent so a ProfileDetail action (opened from the
     deck) can replay the SAME animated fling for the top card instead of a silent
     advance (owner: "now it shows nothing, suddenly next card"). Assigned every
     render so it always closes over the current `top` + entitlement flags; refs
     aren't reactive so this is safe to set in the body. */
  if (flingRef) flingRef.current = flingGated;

  const onDown = (e) => {
    if (leaving || holding) return;
    start.current = { x: e.clientX, y: e.clientY };
    setDrag({ dx: 0, dy: 0, on: true });
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!start.current) return;
    setDrag({ dx: e.clientX - start.current.x, dy: e.clientY - start.current.y, on: true });
  };
  const onUp = (e) => {
    if (!start.current) return;
    const { dx, dy } = drag;
    start.current = null;
    if (Math.abs(dx) < 6 && Math.abs(dy) < 6) {
      setDrag({ dx: 0, dy: 0, on: false });
      // tap: side thirds page the photos (stories pattern), middle opens the profile
      const r = e.currentTarget.getBoundingClientRect();
      const fx = (e.clientX - r.left) / Math.max(1, r.width);
      if (topPhotos.length > 1 && fx >= 2 / 3) { setShot((i) => Math.min(topPhotos.length - 1, i + 1)); return; }
      if (topPhotos.length > 1 && fx <= 1 / 3) { setShot((i) => Math.max(0, i - 1)); return; }
      onOpen(top);
      return;
    }
    if (dx > 90) flingGated('like');
    else if (dx < -90) fling('pass');
    else setDrag({ dx: 0, dy: 0, on: false });
  };

  const stampOp = Math.min(Math.abs(drag.dx) / 80, 1);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', margin: '4px 14px 10px', minHeight: 0 }} data-screen-label="Classic deck">
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {queue.length === 0 ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, border: '1px dashed var(--border-bright)', borderRadius: 24, textAlign: 'center', padding: 30 }}>
            <span className="eyebrow">{t(live ? 'emptyDeckTitle' : 'deckEmptyTitle')}</span>
            <span style={{ fontFamily: 'var(--font-body)', color: 'var(--text-3)', fontSize: 13.5 }}>{t(live ? 'emptyDeckNote' : 'deckEmptyNote')}</span>
            <button className="flm-cta is-sm" style={{ padding: '0 22px' }} onClick={onReset}>{t(live ? 'refresh' : 'deckReset')}</button>
          </div>
        ) : null}

        {queue.slice(0, 3).reverse().map((p) => {
          const i = queue.indexOf(p);
          const isTop = i === 0;
          const isLeaving = leaving && leaving.id === p.id;
          // HOLD phase: this is the top card and its spark burst is playing while it
          // stays STATIONARY (no fly transform yet). A subtle scale-up pulse only.
          const isHolding = holding && holding.id === p.id;
          const dx = isTop ? drag.dx : 0;
          const isSparkLeaving = isLeaving && leaving.dir === 'spark';
          let transform = `translate(${dx}px, ${isTop ? drag.dy * 0.3 : i * 5}px) rotate(${dx / 16}deg) scale(${isHolding ? 1.03 : 1 - i * 0.04})`;
          if (isLeaving) {
            // Spark is a super-like — it must NOT fly LEFT like Pass.
            // Like → right + tilt, Pass → left + tilt, Spark → straight UP with a
            // slight scale-up (the super-like convention), clearly distinct.
            // Spark flies up a shorter distance (-440 vs an off-screen leap) so the
            // card stays VISIBLE for most of the exit while its burst plays over it —
            // it lifts + glows + bursts together, then clears, rather than vanishing
            // instantly and leaving a burst over an empty deck (owner feedback).
            transform = isSparkLeaving
              ? 'translate(0px, -440px) rotate(0deg) scale(1.06)'
              : `translate(${leaving.dir === 'like' ? 480 : -480}px, -40px) rotate(${leaving.dir === 'like' ? 22 : -22}deg)`;
          }
          return (
            <div key={p.id} className="flm-card"
              onPointerDown={isTop ? onDown : undefined} onPointerMove={isTop ? onMove : undefined} onPointerUp={isTop ? onUp : undefined}
              style={{
                borderRadius: 24, transform, zIndex: (isSparkLeaving || isHolding) ? 25 : 10 - i,
                // Exit easing matches fling()'s EXIT_MS so the card is still in flight
                // when the queue advances. Spark eases over a longer beat + gains a
                // love/pink glow so it visibly out-classes a normal like. During the
                // HOLD the card pulses on a quick spring (the subtle scale-up) — it
                // must NOT translate, so the burst plays clearly centered on it first.
                transition: drag.on && isTop && !isLeaving ? 'none' : isHolding ? 'transform 0.26s var(--ease-spring)' : `transform ${isSparkLeaving ? '0.56s' : '0.36s'} var(--ease-out-expo)`,
                boxShadow: (isSparkLeaving || isHolding) ? '0 0 60px oklch(0.71 0.2 354 / 0.7)' : undefined,
                opacity: i > 1 ? 0 : 1, cursor: isTop ? 'grab' : 'default',
              }}>
              {/* Spark burst — anchored INSIDE the card so it travels WITH it. It fires
                  during the HOLD (card still, burst plays centered) and keeps playing
                  into the FLY (the up-lift), so the tail finishes as the card lifts off.
                  Keyed on `burst` so each spark remounts a fresh ~600ms one-shot;
                  pointer-events:none; reduced-motion collapses it for free. */}
              {(isSparkLeaving || isHolding) && burst > 0 ? (
                <span key={burst} style={{ position: 'absolute', left: '50%', top: '46%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 6 }}>
                  <SparkBurst size={240} />
                </span>
              ) : null}
              <GradPhoto profile={p} photo={isTop && p.userId ? topPhotos[Math.min(shot, topPhotos.length - 1)] ?? p.photoUrl : p.photoUrl} w={1024} initialSize="110px" style={{ position: 'absolute', inset: 0 }}>
                <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.35), transparent 28%, transparent 52%, rgba(0,0,0,0.88) 88%)', zIndex: 2 }}></span>

                {/* YES / PASS stamps */}
                <span style={{ position: 'absolute', top: 28, left: 20, zIndex: 4, padding: '4px 12px', border: '3px solid var(--accent)', borderRadius: 10, color: 'var(--accent)', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 26, transform: 'rotate(-12deg)', background: 'rgba(0,0,0,0.4)', opacity: isTop && dx > 0 ? stampOp : 0 }}>{t('like').toUpperCase()}</span>
                <span style={{ position: 'absolute', top: 28, right: 20, zIndex: 4, padding: '4px 12px', border: '3px solid var(--text-2)', borderRadius: 10, color: 'var(--text-2)', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 26, transform: 'rotate(12deg)', background: 'rgba(0,0,0,0.4)', opacity: isTop && dx < 0 ? stampOp : 0 }}>{t('pass').toUpperCase()}</span>

                <PhotoSegments total={p.userId ? (p.photos?.length || (p.photoUrl ? 1 : 0)) : 4} active={isTop ? shot : 0} />
                <MatchRing value={matchPct(p)} faNum={faNum} />

                {/* voice pill — live cards only when a REAL voice intro exists */}
                {!p.userId || p.voiceUrl ? (
                  <span className="flm-glass" style={{ position: 'absolute', left: 16, bottom: 118, zIndex: 3, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 13px', borderRadius: 99 }}>
                    <Icon name="play" size={14} color="var(--accent)" fill="var(--accent)" />
                    <VoiceWave bars={13} height={14} />
                    <span className="flm-mono" style={{ fontSize: 9, color: '#fff' }}>{faNum(fmtVoice(p.voice))}</span>
                  </span>
                ) : null}

                <div style={{ position: 'absolute', left: 16, right: 16, bottom: 18, zIndex: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="flm-display" style={{ fontSize: 30, color: '#fff' }}>{p.name}, {faNum(p.age)}</span>
                    {p.verified ? <Verif size={19} /> : null}
                    {p.isPremium ? <PremiumBadge t={t} /> : null}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3, color: 'var(--text-2)', fontSize: 13.5 }}>
                    <Icon name="mapPin" size={13} />
                    <span>{locationLine(p, t, faNum)}{p.online ? ' · ' + t('online').toLowerCase() : ''}</span>
                    {p.online ? <span className="flm-dot" style={{ width: 5, height: 5 }}></span> : null}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {p.interests.slice(0, 3).map((tag) => (
                      <span key={tag} className="flm-glass" style={{ padding: '5px 11px', borderRadius: 99, fontSize: 12, fontFamily: 'var(--font-body)', color: 'var(--text)' }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </GradPhoto>
            </div>
          );
        })}
        {/* (Spark burst now renders INSIDE the leaving card above, so it travels
            with the card as it lifts instead of floating at a fixed deck point.) */}
      </div>

      {/* action row — OWNER DECISION: Rewind · Pass · Like · Spark · Message(premium).
          Rewind is the premium undo (leftmost, gold). Like stays the emphasized
          center action (largest, glowing). Spark + the premium Message are satellites
          either side of it. Message (rightmost, gold) is the paid pre-match reach-out,
          acting on the current top card. Gift moved into ProfileDetail; Star retired
          (kept behind STAR_ENABLED). Slightly tighter gap to seat 5 buttons.
          CROWNS: Rewind + Message wear the gold crown ONLY when LOCKED — i.e. the
          viewer's plan doesn't grant them (canRewind / canMessage false). A plan that
          already includes the feature shows a clean (no-crown) button that works; a
          locked tap routes to onUpgrade (the upgrade ladder shows the right tier). */}
      <div style={{ flex: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, padding: '14px 0 2px' }}>
        <ActionBtn icon="rewind" size={48} iconSize={19} color="var(--gold)" ring="var(--gold)" crown={!canRewind}
          onClick={() => { if (!canRewind) { haptic('light'); onUpgrade && onUpgrade('spark'); return; } haptic('light'); onRewind(); }}
          style={{ opacity: live ? 1 : 0.4 }} />
        <ActionBtn icon="x" size={56} iconSize={23} color="var(--text-2)" ring="var(--text-2)" onClick={() => fling('pass')} />
        <ActionBtn icon="heart" size={66} iconSize={27} color="#000" bg="var(--accent)" border="none" ring="var(--accent)" glow fill onClick={() => flingGated('like')} />
        <ActionBtn icon="zap" size={56} iconSize={22} color="var(--love)" ring="var(--love)" bg="var(--love-tint)" border="1px solid oklch(0.71 0.20 354 / 0.55)" fill onClick={() => flingGated('spark')} />
        {STAR_ENABLED ? (
          <ActionBtn icon="star" size={48} iconSize={20} color="var(--gold)" onClick={() => fling('star')} />
        ) : null}
        {/* premium Message-before-match — acts on the current top card; a satellite
            sized like Rewind. onMessage owns the entitlement gate (open vs upgrade).
            Crown shown only when LOCKED (canMessage false). */}
        <PremiumMessageBtn t={t} size={48} iconSize={18} locked={!canMessage} onClick={() => { if (top) onMessage && onMessage(top); }} />
      </div>
      {/* subtle inline note — "Nothing to undo" when there's no prior swipe to rewind */}
      {rewindNote ? (
        <div style={{ flex: 'none', textAlign: 'center', padding: '2px 0 0', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-3)', animation: 'flmFadeIn 0.25s var(--ease-out-expo)' }}>
          {rewindNote}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- B · The Salon — "Seven people, chosen for you." ---------- */
function SalonView({ queue, t, faNum, onOpen, onLike, onUpgrade }) {
  const left = useSalonCountdown();
  const big = queue[0];
  const small = queue.slice(1, 3);

  const SalonCard = ({ p, big = false }) => (
    <div onClick={() => onOpen(p)} style={{ position: 'relative', borderRadius: 18, overflow: 'hidden', height: big ? 240 : 116, border: '1px solid var(--border)', cursor: 'pointer' }}>
      <GradPhoto profile={p} photo={p.photoUrl} initialSize={big ? '7rem' : '4rem'} style={{ position: 'absolute', inset: 0 }}>
        <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.85))', zIndex: 2 }}></span>
        <div style={{ position: 'absolute', left: 14, right: 14, bottom: 12, zIndex: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <b className="flm-display" style={{ fontSize: big ? 22 : 17, color: '#fff' }}>{p.name}, {faNum(p.age)}</b>
            {p.verified ? <Verif size={15} /> : null}
          </div>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-2)' }}>
            {big ? `${t('matchPct', { n: faNum(matchPct(p)) })} · ${p.job}`
              : p.online ? t('onlineNow') : `${faNum(p.dist)} ${t('km').toLowerCase()} · ${p.job}`}
          </span>
        </div>
        <button className="flm-ghost-btn" onClick={(e) => { e.stopPropagation(); onLike(p); }} style={{ position: 'absolute', insetInlineEnd: 12, bottom: 12, zIndex: 3, width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, color: '#000', boxShadow: '0 0 18px rgba(255,212,145,0.4)', cursor: 'pointer' }}>
          <Icon name="heart" size={15} fill="#000" color="#000" />
        </button>
      </GradPhoto>
    </div>
  );

  return (
    <div className="flm-scroll" style={{ padding: '0 16px 16px', position: 'relative' }} data-screen-label="The Salon">
      <GlowBlob color="is-mint" size={180} x="92%" y="14%" o={0.8} />
      <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0 0' }}>
        <span className="flm-eyebrow">
          <span className="flm-dot is-love"></span>
          <span className="flm-mono" style={{ color: 'var(--text-2)' }}>{t('salonEyebrow')}</span>
        </span>
        <span className="flm-mono" style={{ color: 'var(--gold)', fontSize: 9 }}>{t('salonResets', { time: faNum(left) })}</span>
      </div>
      <h1 className="flm-display" style={{ fontSize: 27, color: 'var(--text)', margin: '10px 0 2px', position: 'relative', zIndex: 2 }}>
        {t('salonTitle1')}<br />{t('salonTitle2')}
      </h1>
      <span style={{ fontFamily: 'var(--font-body)', fontSize: 13.5, color: 'var(--text-2)', position: 'relative', zIndex: 2 }}>{t('salonSub')}</span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14, position: 'relative', zIndex: 2 }}>
        {big ? <SalonCard p={big} big /> : null}
        {small.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {small.map((p) => <SalonCard key={p.id} p={p} />)}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, height: 84, border: '1px dashed var(--border-bright)', borderRadius: 16, background: 'var(--surface)' }}>
            <span className="flm-mono" style={{ fontSize: 9 }}>{t('salonMore', { n: faNum(4) })}</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-3)' }}>{t('salonUnlock')}</span>
          </div>
          <div onClick={onUpgrade} className="flm-hairline" style={{ position: 'relative', flex: 1.3, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, height: 84, padding: '0 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, cursor: 'pointer' }}>
            <span className="flm-mono" style={{ color: 'var(--accent)', fontSize: 9 }}>{t('salonBlack')}</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.35, color: 'var(--text-2)' }}>{t('salonBlackBody')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- C · Orbit — people float around you by distance ---------- */
function OrbitView({ queue, t, faNum, onOpen, onLike }) {
  const [peek, setPeek] = React.useState(queue[0] || null);
  return (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }} data-screen-label="Orbit discover">
      {/* spinning rings */}
      {[150, 260, 370].map((d, i) => (
        <span key={d} style={{ position: 'absolute', left: '50%', top: '46%', width: d, height: d, transform: 'translate(-50%,-50%)', borderRadius: '50%', border: '1px dashed rgba(51,51,51,0.85)', animation: `flmSpinSlow ${28 + i * 16}s linear infinite` }}>
          <span style={{ position: 'absolute', top: -3, left: '50%', width: 5, height: 5, borderRadius: '50%', background: 'var(--primary-light)' }}></span>
        </span>
      ))}
      <GlowBlob color="is-purple" size={300} x="50%" y="46%" o={0.6} />

      {/* you, center */}
      <div style={{ position: 'absolute', left: '50%', top: '46%', transform: 'translate(-50%,-50%)', zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 62, height: 62, borderRadius: '50%', background: 'var(--primary)', border: '2px solid var(--accent)', boxShadow: 'var(--glow-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 24, color: '#fff' }}>
          {t('youCenter')[0].toUpperCase()}
        </div>
        <span className="flm-mono" style={{ fontSize: 8, color: 'var(--accent)' }}>{t('youCenter')}</span>
      </div>

      {/* orbiting people */}
      {queue.slice(0, 8).map((p, i) => {
        const sz = Math.max(38, 72 - p.dist * 4);
        return (
          <div key={p.id} onClick={() => setPeek(p)} style={{
            position: 'absolute', left: p.pos.x + '%', top: p.pos.y + '%', zIndex: 4,
            transform: 'translate(-50%,-50%)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            animation: `flmOrbitFloat ${4 + (i % 3)}s ease-in-out ${i * 0.4}s infinite`,
          }}>
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              {p.online ? <span style={{ position: 'absolute', inset: -5, borderRadius: '50%', border: '1px solid var(--accent)', animation: 'flmRingPulse 2.4s var(--ease-out-expo) infinite' }}></span> : null}
              <GradPhoto profile={p} photo={p.photoUrl} initialSize={sz * 0.4 + 'px'} style={{ width: sz, height: sz, borderRadius: '50%', border: peek && peek.id === p.id ? '2px solid var(--accent)' : '1px solid var(--border-bright)' }} />
            </span>
            <span className="flm-mono" style={{ fontSize: 7.5 }}>{faNum(p.dist)} {t('km').toLowerCase()}</span>
          </div>
        );
      })}

      {/* peek card */}
      <div style={{ position: 'absolute', bottom: 12, insetInline: 16, zIndex: 10 }}>
        {peek ? (
          <div key={peek.id} className="flm-glass" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 13, animation: 'flmSlideUp 0.3s var(--ease-out-expo)' }}>
            <Avatar profile={peek} photo={peek.photoUrl} size={50} onClick={() => onOpen(peek)} />
            <div onClick={() => onOpen(peek)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <b style={{ fontFamily: 'var(--font-body)', fontSize: 15.5, color: 'var(--text)' }}>{peek.name}, {faNum(peek.age)}</b>
                {peek.verified ? <Verif size={14} /> : null}
              </div>
              <span className="flm-mono" style={{ fontSize: 8.5 }}>
                {faNum(peek.dist)} {t('km')} · {peek.job}{peek.online ? ' · ' + t('online') : ''}
              </span>
            </div>
            <ActionBtn icon="x" size={38} iconSize={15} color="var(--text-2)" bg="transparent" onClick={() => setPeek(null)} />
            <ActionBtn icon="heart" size={42} iconSize={17} color="#000" bg="var(--accent)" border="none" fill style={{ boxShadow: '0 0 18px rgba(255,212,145,0.4)' }} onClick={() => { onLike(peek); setPeek(null); }} />
          </div>
        ) : (
          <span className="flm-mono" style={{ display: 'block', textAlign: 'center', fontSize: 8.5 }}>{t('orbitTap')}</span>
        )}
      </div>
    </div>
  );
}

/* ---------- Likes overlay — "Who chose you" artboard ---------- */
function LikesOverlay({ open, t, faNum, premium, onClose, onUpgrade, onOpen }) {
  const demo = isDemo();
  // Live "who chose you": count for everyone, full list when premium. No mock fallback.
  const [live, setLive] = React.useState(null);
  const [err, setErr] = React.useState(false);
  const load = React.useCallback(() => {
    setErr(false);
    getLikesCount()
      .then((r) => setLive((s) => ({ ...(s || {}), count: r.count })))
      .catch(() => setErr(true));
    if (premium) {
      getLikes()
        // keep each liker's `spark` flag alongside the mapped card so the premium
        // grid can mark super-likes (getLikes items are { likedAt, spark, profile }).
        .then((r) => setLive((s) => ({ ...(s || {}), count: s?.count ?? r.items.length, items: r.items.map((x) => ({ ...apiToCard(x.profile), spark: Boolean(x.spark) })) })))
        .catch(() => setErr(true));
    } else {
      // free teaser: REAL faces, anonymous (photo only — no names/ids travel);
      // blurred client-side in Tile below. Failures stay silent: the count grid
      // simply keeps its gradient placeholders.
      getLikesPreview()
        .then((r) => setLive((s) => ({ ...(s || {}), count: s?.count ?? 0, preview: r.items.map((x) => mediaUrl(x.photoUrl)) })))
        .catch(() => {});
    }
  }, [premium]);
  React.useEffect(() => {
    if (!open || demo) return;
    load();
  }, [open, demo, load]);
  if (!open) return null;
  const loading = !demo && !err && live === null;
  const mockLikers = demo ? FLM_DATA.profiles.filter((p) => LIKES_YOU.has(p.id)) : [];
  const likesCount = demo ? mockLikers.length : live?.count ?? 0;
  // demo: the artboard set · live premium: real profiles · live free: real COUNT
  // + real faces from /likes/preview (anonymous, heavily blurred below); gradient
  // placeholders only pad likers the preview couldn't cover (never fake people)
  const previewPhotos = !demo && !premium ? live?.preview ?? [] : [];
  const likers = demo
    ? mockLikers
    : premium
      ? (live?.items ?? [])
      : Array.from({ length: Math.min(likesCount, 6) }, (_, i) => ({ id: 'ph' + i, name: '?', grad: GRADS[i % GRADS.length], photoUrl: previewPhotos[i] ?? null }));
  const freeN = demo ? 2 : 0;

  const Tile = ({ p, blur }) => (
    <div onClick={() => (blur ? onUpgrade() : onOpen(p))} style={{ position: 'relative', height: 168, borderRadius: 16, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer' }}>
      {/* heavy blur + slight scale hides identity AND the soft edge bleed (container clips) */}
      <GradPhoto profile={p} photo={p.photoUrl} initialSize="4.5rem" style={{ position: 'absolute', inset: 0, filter: blur ? 'blur(18px) saturate(0.85)' : 'none', transform: blur ? 'scale(1.12)' : 'none' }} />
      <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.8))', zIndex: 2 }}></span>
      {blur ? (
        <span style={{ position: 'absolute', inset: 0, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="lock" size={17} color="#fff" />
          </span>
        </span>
      ) : (
        <div style={{ position: 'absolute', insetInlineStart: 11, bottom: 9, zIndex: 3 }}>
          <b style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#fff' }}>{p.name}</b>
        </div>
      )}
      {/* corner like icon — a Spark liker (super-like) swaps the heart for a
          lightning badge with a love/pink glow + soft pulse ring so it stands
          out. PREMIUM (unlocked) view only: free tiles are blurred + anonymous,
          so no spark detail leaks there. */}
      {!blur && p.spark ? (
        <span style={{ position: 'absolute', top: 7, insetInlineEnd: 7, zIndex: 3, display: 'inline-flex' }}>
          {/* one-shot-feel steady pulse ring (reuses flmRingPulse, infinite) */}
          <span aria-hidden="true" style={{ position: 'absolute', inset: -3, borderRadius: '50%', border: '1.5px solid var(--love)', animation: 'flmRingPulse 2s var(--ease-out-expo) infinite' }}></span>
          <span style={{
            width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--love-tint)', border: '1px solid oklch(0.71 0.20 354 / 0.6)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            boxShadow: '0 0 12px oklch(0.71 0.2 354 / 0.7)',
          }}>
            <Icon name="zap" size={13} fill="var(--love)" color="var(--love)" />
          </span>
        </span>
      ) : (
        <span style={{ position: 'absolute', top: 8, insetInlineEnd: 8, zIndex: 3, color: 'var(--love)' }}>
          <Icon name="heart" size={15} fill="var(--love)" color="var(--love)" style={{ filter: 'drop-shadow(0 0 8px oklch(0.71 0.2 354 / 0.8))' }} />
        </span>
      )}
    </div>
  );

  return (
    <FullOverlay open={true} z={64}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px 4px', flex: 'none' }}>
        <button className="flm-ghost-btn flm-flip" onClick={onClose} style={{ color: 'var(--text-secondary)' }}><Icon name="arrowLeft" size={22} /></button>
        <RoundBtn icon="sliders" size={36} iconSize={17} />
      </div>
      <div className="flm-scroll" style={{ padding: '0 16px 20px', position: 'relative' }} data-screen-label="Likes you">
        <GlowBlob color="is-love" size={200} x="92%" y="6%" o={0.7} />
        <h1 className="flm-display" style={{ fontSize: 26, color: 'var(--text)', margin: '4px 0 2px', position: 'relative', zIndex: 2 }}>
          {t('likesTitle1', { n: faNum(likesCount) })}<br />{t('likesTitle2')}
        </h1>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 13.5, color: 'var(--text-2)', position: 'relative', zIndex: 2 }}>
          {demo ? t('likesSub', { n: faNum(freeN) }) : !premium ? t('revealNote') : ''}
        </span>

        <div style={{ display: 'flex', gap: 8, margin: '12px 0', position: 'relative', zIndex: 2 }}>
          <span className="flm-chip is-on" style={{ fontSize: 12.5 }}>{t('likedYouTab', { n: faNum(likesCount) })}</span>
          {demo ? (
            <React.Fragment>
              <span className="flm-chip" style={{ fontSize: 12.5 }}>{t('favoritesTab', { n: faNum(9) })}</span>
              <span className="flm-chip" style={{ fontSize: 12.5 }}>{t('rosesTab', { n: faNum(3) })}</span>
            </React.Fragment>
          ) : null}
        </div>

        {loading ? (
          <GridSkeleton count={6} cols={2} style={{ padding: 0, marginTop: 2 }} />
        ) : err ? (
          <StatePanel title={t('errTitle')} note={t('errNote')} actionLabel={t('retry')} onAction={load} style={{ minHeight: 220 }} />
        ) : likers.length === 0 ? (
          <StatePanel title={t('likesTitle2')} note={t('likesEmptyNote')} style={{ minHeight: 220 }} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, position: 'relative', zIndex: 2 }}>
            {likers.map((p, idx) => (
              <div key={p.id} className="flm-enter" style={{ '--i': Math.min(idx, 10) }}>
                <Tile p={p} blur={!premium && idx >= freeN} />
              </div>
            ))}
          </div>
        )}

        {!premium && likesCount > 0 ? (
          <button className="flm-cta is-sm" onClick={onUpgrade} style={{ width: '100%', marginTop: 12, gap: 8 }}>
            <Icon name="crown" size={15} fill="currentColor" />
            {demo ? t('revealAllCta', { n: faNum(likesCount) }) : t('likesPaywallCta')}
          </button>
        ) : null}
      </div>
    </FullOverlay>
  );
}

/* ---------- Filters sheet ---------- */
function FiltersSheet({ open, t, faNum, onClose, onApply }) {
  const [dist, setDist] = React.useState(25); // km · GLOBAL_KM = worldwide
  const [age, setAge] = React.useState(32);
  const [show, setShow] = React.useState('everyone'); // 'women' | 'men' | 'everyone'
  const [vOnly, setVOnly] = React.useState(true); // visual-only — no API field yet
  const [pref, setPref] = React.useState(null); // live preference from GET /v1/me (null → mock mode)
  const fetched = React.useRef(false);

  // First open: seed the controls from the server preference (demo keeps the
  // artboard defaults and never persists).
  React.useEffect(() => {
    if (!open || fetched.current || isDemo()) return;
    fetched.current = true;
    getMe()
      .then((res) => {
        const p = res?.preference;
        if (!p) return;
        setPref(p);
        if (Number.isFinite(p.distanceKm)) setDist(p.distanceKm);
        if (Number.isFinite(p.ageMax)) setAge(p.ageMax);
        if (p.showGender) setShow(p.showGender);
      })
      .catch(() => {});
  }, [open]);

  const isGlobal = dist >= GLOBAL_KM;
  // Close = apply: persist when signed in (silent otherwise); single age slider → 18..age.
  const close = () => {
    if (pref) onApply({ showGender: show || pref.showGender, ageMin: 18, ageMax: age, distanceKm: dist });
    onClose();
  };
  return (
    <Sheet open={open} onClose={close}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.4rem', color: 'var(--text)', margin: '4px 0 20px' }}>{t('filters')}</h3>
      {/* Show me — the wizard's chip row, persisted with the rest of the sheet */}
      <MonoLabel color="var(--text-muted)">{t('obShowMe')}</MonoLabel>
      <div style={{ display: 'flex', gap: 8, margin: '8px 0 22px' }}>
        {[['women', t('woman')], ['men', t('man')], ['everyone', t('everyone')]].map(([id, label]) => (
          <button key={id} className={'flm-chip' + (show === id ? ' is-on' : '')} onClick={() => setShow(id)}
            style={{ flex: 1, justifyContent: 'center', height: 44, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <MonoLabel color="var(--text-muted)">{t('distance')}</MonoLabel>
        <MonoLabel color="var(--accent)">{isGlobal ? t('filterGlobal') : `${faNum(dist)} ${t('km')}`}</MonoLabel>
      </div>
      {/* top stop = GLOBAL: persists the GLOBAL_KM sentinel → server skips distance filtering */}
      <input type="range" className="flm-range" min="1" max="101" value={isGlobal ? 101 : Math.min(dist, 100)}
        onChange={(e) => { const v = +e.target.value; setDist(v > 100 ? GLOBAL_KM : v); }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '22px 0 8px' }}>
        <MonoLabel color="var(--text-muted)">{t('ageRange')}</MonoLabel>
        <MonoLabel color="var(--accent)">{faNum(18)}–{faNum(age)}</MonoLabel>
      </div>
      <input type="range" className="flm-range" min="20" max="60" value={age} onChange={(e) => setAge(+e.target.value)} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '24px 0 6px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-body)', color: 'var(--text)' }}>
          <Verif size={17} /> {t('verified')}
        </span>
        <button onClick={() => setVOnly(!vOnly)} style={{ width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', position: 'relative', background: vOnly ? 'var(--accent)' : 'var(--border-bright)', transition: 'background 0.2s ease' }}>
          <span style={{ position: 'absolute', top: 3, left: vOnly ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#000', transition: 'left 0.2s var(--ease-out-expo)' }}></span>
        </button>
      </div>
      <Button variant="primary" onClick={close} style={{ width: '100%', marginTop: 22 }}>{t('done')}</Button>
    </Sheet>
  );
}

/* ---------- Discover screen ---------- */
/* ---- live deck wiring (data source only; UI untouched) ---- */
const GRADS = [
  ['#2D0041', '#7A1FA2'], ['#4B006E', '#1a0026'], ['#3a0a54', '#12001c'],
  ['#5e1287', '#240035'], ['#1f0030', '#4B006E'], ['#141414', '#2D0041'],
];
const hashOf = (s) => [...String(s)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
const slugLabel = (slug) => String(slug).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
/** Map an API candidate card to the prototype card shape the deck renders. */
function apiToCard(c) {
  const h = hashOf(c.userId);
  const photoUrl = mediaUrl(c.photoUrl);
  const photos = (c.photos || []).map((u) => mediaUrl(u));
  // Register blur-up placeholders: cover + each gallery photo (aligned arrays).
  registerBlur(photoUrl, c.blurHash);
  (c.photoBlurs || []).forEach((hash, i) => registerBlur(photos[i], hash));
  return {
    id: c.userId,
    userId: c.userId, // marks the card as live (mock cards have no userId)
    name: c.name,
    age: c.age,
    dist: c.distKm ?? '—',
    // coarse place fallback for the location line when distance can't resolve
    city: c.city ?? null,
    country: c.country ?? null,
    isPremium: Boolean(c.isPremium),
    grad: GRADS[h % GRADS.length],
    photoUrl,
    photos,
    verified: Boolean(c.verified),
    online: Boolean(c.online),
    job: c.job || '',
    voice: c.voice ?? 0,
    voiceUrl: mediaUrl(c.voiceUrl),
    bio: c.bio || '',
    interests: (c.interests || []).map(slugLabel),
    common: c.common ?? 0,
    pos: { x: 15 + (h % 60), y: 18 + ((h >> 3) % 60) },
  };
}
const toApiDecision = (dir) => (dir === 'spark' || dir === 'star' ? 'spark' : dir === 'like' ? 'yes' : 'pass');

/* Cross-mount deck cache: tabs remount on switch, so without this every return
   to Discover refetched + flashed a spinner. We restore the last queue instantly
   (if recent) and still refetch in the background to reconcile. */
let deckCache = { items: null, at: 0 };
const DECK_CACHE_MS = 90_000;

function DiscoverScreen({ t, faNum, mode, setMode, premium, onMatch, onUpgrade }) {
  const demo = isDemo();
  // demo: the artboard set · live: null = loading until the deck answers. Live:
  // hydrate from the recent cache so the tab is instant; a fresh fetch follows.
  const [queue, setQueue] = React.useState(() =>
    demo
      ? FLM_DATA.profiles
      : deckCache.items && Date.now() - deckCache.at < DECK_CACHE_MS
        ? deckCache.items
        : null,
  );
  // Keep the cache in sync with the live queue (post-swipe state included).
  React.useEffect(() => {
    if (!demo && Array.isArray(queue)) deckCache = { items: queue, at: Date.now() };
  }, [queue, demo]);
  const [deckErr, setDeckErr] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [likesOpen, setLikesOpen] = React.useState(false);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  // Rewind (premium undo): remember the just-swiped live card for instant optimistic
  // re-show, and a transient inline note ("Nothing to undo"). Server is the truth.
  const lastSwiped = React.useRef(null);
  const [rewindNote, setRewindNote] = React.useState('');
  const rewindBusy = React.useRef(false);
  // "See who likes you" badge count — demo seeds the mock set, live polls the API.
  const [likesCount, setLikesCount] = React.useState(() => (demo ? LIKES_YOU.size : 0));
  // Daily-limits HUD source (Sparks left, likes left for free users). Defensive:
  // null until/unless the subscription call answers with entitlements+usage; the
  // HUD renders nothing in that case. `refreshSub` re-reads it after each swipe so
  // the remaining counts tick down in step with the deck.
  const [sub, refreshSub, bumpUsage] = useEntitlements(demo);
  // Entitlement flags driving the deck's crowns + the out-of-limit upsell. All are
  // optional-chained against the not-yet-final {entitlements, usage} shape (defaults
  // to "locked / not exhausted" so a missing payload never wrongly blocks an action).
  const ent = sub?.entitlements;
  // Whether the viewer holds the Infinity "message before match" perk. Drives the
  // deck's premium Message button: entitled → open compose + NO crown; not → onUpgrade
  // (upsell) + crown.
  const canMessage = Boolean(ent?.messageBeforeMatch);
  // Whether the viewer's plan grants Rewind (any paid tier). Crown shows only when
  // this is false (free). Defensive default false (free) when entitlements aren't in.
  const canRewind = Boolean(ent?.canRewind);
  // FREE viewers exhaust a daily allowance → tapping Like/Spark then upsells instead
  // of firing a 403. Source of truth is the DRIP BUCKET balance (deriveLimits), not
  // today's consumption count — otherwise bonus charges (daily streak, referral,
  // recharge) sit unusable behind a "you've used your N today" gate. Premium
  // ('unlimited' likes) never exhausts; null balance (no info yet) → don't block.
  const lim = deriveLimits(sub);
  const likesExhausted = !lim?.unlimitedLikes && lim?.likesLeft === 0;
  const sparksExhausted = lim?.sparksLeft === 0;
  // Transient out-of-limit note shown when a free, exhausted Like/Spark tap upsells.
  const [limitNote, setLimitNote] = React.useState('');
  const limitTimer = React.useRef(null);
  React.useEffect(() => () => { if (limitTimer.current) clearTimeout(limitTimer.current); }, []);
  // Out-of-limit tap (from the deck row, a right-swipe release, or a ProfileDetail
  // action): surface a brief note + open the upgrade flow (owner: don't fire a swipe
  // that 403s silently — upsell on the tap).
  const handleOutOfLimit = (kind) => {
    setLimitNote(t(kind === 'sparks' ? 'outOfSparks' : 'outOfLikes'));
    if (limitTimer.current) clearTimeout(limitTimer.current);
    limitTimer.current = setTimeout(() => setLimitNote(''), 2400);
    onUpgrade && onUpgrade();
  };
  // Imperative handle to the deck's gated fling — set by SwipeDeck each render so a
  // ProfileDetail action (opened from the deck) replays the SAME animated fling for
  // the top card (records the swipe itself) instead of a silent advance.
  const deckFlingRef = React.useRef(null);
  // Deck-level compose controller (the SwipeDeck has no ProfileDetail of its own).
  // Shares the same Sheet + send/403/409 flow ProfileDetail uses.
  const deckCompose = useMessageCompose({ t, onUpgrade });
  // Premium Message tap from the deck row, acting on the current top card: entitled
  // viewers open the compose sheet; everyone else routes to the upgrade flow.
  const handleDeckMessage = (p) => {
    if (!p?.userId) return;
    if (canMessage) deckCompose.open(p);
    else onUpgrade && onUpgrade('infinity'); // Infinity grants message-before-match
  };

  // Header heart glow/badge source — refreshed on deck load and after the
  // overlay closes (answering a liker removes them from the pending count).
  const refreshLikesCount = React.useCallback(() => {
    if (demo) { setLikesCount(LIKES_YOU.size); return; }
    getLikesCount().then((r) => setLikesCount(r?.count ?? 0)).catch(() => {});
  }, [demo]);

  // Live candidates — empty stays empty (designed state below), errors retry.
  const loadDeck = React.useCallback(() => {
    if (demo) { setQueue(FLM_DATA.profiles); return; }
    setDeckErr(false);
    getDeck()
      .then((res) => setQueue((res?.items ?? []).map(apiToCard)))
      .catch(() => { setDeckErr(true); setQueue((q) => q ?? []); });
  }, [demo]);
  React.useEffect(() => { loadDeck(); refreshLikesCount(); }, [loadDeck, refreshLikesCount]);

  // EAGER likes-count refresh — fixes "I liked them but their heart stayed empty
  // because they were already in the app". A like that arrives while the recipient
  // is on the deck now surfaces without a manual reload: poll every 35s, plus
  // refresh whenever the app/tab regains focus or the Telegram viewport wakes
  // (cheap GETs; the count query is the single source of truth). Mounting this
  // component == the Discover tab is active, so this only runs while on Discover.
  React.useEffect(() => {
    if (demo) return;
    const id = setInterval(refreshLikesCount, 35000);
    const onWake = () => { if (document.visibilityState !== 'hidden') refreshLikesCount(); };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    // Telegram Mini App viewport wake (foreground / expand) — guarded; no-ops off-platform.
    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    tg?.onEvent?.('viewportChanged', onWake);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
      tg?.offEvent?.('viewportChanged', onWake);
    };
  }, [demo, refreshLikesCount]);

  const handleSwipe = (dir, p) => {
    setQueue((q) => (q || []).filter((x) => x.id !== p.id));
    if (p.userId) {
      // live card → record the swipe; mutual yes shows the match moment.
      // Errors stay silent (the card already left the deck) except daily-limit
      // refusals, which route to the upgrade flow.
      lastSwiped.current = p; // newest swipe → rewind candidate (server is the truth)
      setRewindNote('');
      // Count it locally NOW so the HUD + exhausted gate update before the next
      // tap (refreshSub below reconciles to the server's authoritative tally).
      if (dir !== 'pass') bumpUsage(dir);
      postSwipe(p.userId, toApiDecision(dir))
        .then((res) => { if (res?.match) onMatch(p); })
        .catch((err) => { if (/limit/i.test(err?.message || '')) onUpgrade(); })
        // reconcile the HUD's remaining-allowance after a like/spark lands
        .finally(() => { if (dir !== 'pass') refreshSub(); });
    } else if (demo && (dir === 'like' || dir === 'spark' || dir === 'star') && LIKES_YOU.has(p.id)) {
      onMatch(p);
    }
  };

  // ProfileDetail action router (owner: "shrink back, THEN the deck swipe anim").
  // The detail overlay has already shrunk/faded toward the deck by the time this
  // fires. If the profile is the deck's CURRENT top card and the deck exposed its
  // fling, close the overlay and let the deck replay its animated fling for that
  // card — the fling's own commit records the swipe (no double-record here). Any
  // other profile (e.g. opened from the Likes overlay, where it's not the deck top)
  // falls back to the direct record + close, exactly as before.
  const actFromDetail = (dir, p) => {
    const topId = queue && queue[0] ? queue[0].id : null;
    if (deckFlingRef.current && p && p.id === topId) {
      setDetail(null);
      deckFlingRef.current(dir); // animates + records (or upsells if out-of-limit)
    } else {
      handleSwipe(dir, p);
      setDetail(null);
    }
  };

  // Rewind (premium): free users → upgrade sheet (same routing as the daily-limit
  // path). Premium → undo the last swipe server-side and unshift the returned card
  // back to the FRONT of the deck. 'nothing_to_undo' shows a transient inline note.
  const handleRewind = () => {
    if (demo) { setRewindNote(t('rewindNothing')); setTimeout(() => setRewindNote(''), 2200); return; }
    if (rewindBusy.current) return;
    rewindBusy.current = true;
    setRewindNote('');
    rewindSwipe()
      .then((res) => {
        if (res?.ok && res.profile) {
          const card = apiToCard(res.profile);
          setQueue((q) => [card, ...(q || []).filter((x) => x.id !== card.id)]);
          lastSwiped.current = null;
        } else {
          // 'matched' → can't undo a match (it stays intact server-side); anything
          // else (e.g. 'nothing_to_undo') falls back to the generic note.
          setRewindNote(t(res?.reason === 'matched' ? 'rewindMatched' : 'rewindNothing'));
          setTimeout(() => setRewindNote(''), 2200);
        }
      })
      .catch((err) => {
        // Premium gate (403 'auth/forbidden') → upgrade sheet; other errors stay quiet.
        if (err instanceof ApiCodedError && err.code === 'auth/forbidden') onUpgrade();
        else if (/premium/i.test(err?.message || '')) onUpgrade();
      })
      .finally(() => { rewindBusy.current = false; });
  };
  const resetDeck = loadDeck;

  const loading = !demo && queue === null && !deckErr;
  // Salon/Orbit hidden for now (MODES_ENABLED) — the deck is the only live view.
  const view = MODES_ENABLED ? mode : 'deck';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, position: 'relative' }} data-screen-label="Discover">
      <ScreenHead title={t('discover')} trailing={
        <React.Fragment>
          {/* mode segmented: deck | salon | orbit (parked behind MODES_ENABLED) */}
          {MODES_ENABLED ? (
            <div style={{ display: 'flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', padding: 3, gap: 2 }}>
              {['deck', 'salon', 'orbit'].map((m) => (
                <button key={m} className="flm-ghost-btn" onClick={() => setMode(m)} style={{
                  padding: '5px 11px', borderRadius: 'var(--radius-pill)',
                  background: mode === m ? 'var(--accent)' : 'transparent',
                  color: mode === m ? '#000' : 'var(--text-3)', transition: 'all 0.2s ease',
                  fontFamily: 'var(--font-mono)', fontSize: '0.55rem', letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>{t(m)}</button>
              ))}
            </div>
          ) : null}
          <LikesHeartBtn count={likesCount} faNum={faNum} t={t} onClick={() => setLikesOpen(true)} />
          <RoundBtn icon="sliders" size={36} iconSize={17} onClick={() => setFiltersOpen(true)} />
        </React.Fragment>
      } />

      {/* daily free-spark claim + streak — the daily-open hook. Refreshes the HUD
          on claim so the new spark shows immediately. */}
      <DailyClaim t={t} faNum={faNum} onClaimed={refreshSub} />

      {/* daily-limits HUD — Sparks left (everyone) + likes left (free). Renders
          nothing until the subscription answers with entitlements+usage. */}
      <LimitsHud sub={sub} t={t} faNum={faNum} onUpgrade={onUpgrade} />

      {loading ? (
        <DeckCardSkeleton style={{ margin: '4px 14px 10px' }} />
      ) : !demo && deckErr && (queue ?? []).length === 0 ? (
        <StatePanel title={t('errTitle')} note={t('errNote')} actionLabel={t('retry')} onAction={loadDeck}
          style={{ flex: 1, margin: '4px 14px 10px' }} />
      ) : view === 'salon'
        ? <SalonView queue={queue ?? []} t={t} faNum={faNum} onOpen={setDetail} onLike={(p) => handleSwipe('like', p)} onUpgrade={onUpgrade} />
        : view === 'orbit'
          ? <OrbitView queue={queue ?? []} t={t} faNum={faNum} onOpen={setDetail} onLike={(p) => handleSwipe('like', p)} />
          : <SwipeDeck queue={queue ?? []} t={t} faNum={faNum} live={!demo} onSwipe={handleSwipe} onReset={resetDeck} onOpen={setDetail} onRewind={handleRewind} onMessage={handleDeckMessage} rewindNote={rewindNote || limitNote}
              onUpgrade={onUpgrade} canRewind={canRewind} canMessage={canMessage}
              likesExhausted={likesExhausted} sparksExhausted={sparksExhausted} onOutOfLimit={handleOutOfLimit} flingRef={deckFlingRef} />}

      {/* deck-level Message-before-match compose sheet (the SwipeDeck row's premium
          Message button opens this; ProfileDetail owns its own instance). */}
      {deckCompose.sheet}

      {detail ? <ProfileDetail profile={detail} t={t} faNum={faNum} onClose={() => setDetail(null)}
        onPass={(p) => actFromDetail('pass', p)} onLike={(p) => actFromDetail('like', p)} onSpark={(p) => actFromDetail('spark', p)}
        onUpgrade={onUpgrade} canMessage={canMessage} /> : null}
      <LikesOverlay open={likesOpen} t={t} faNum={faNum} premium={premium} onClose={() => { setLikesOpen(false); refreshLikesCount(); }} onUpgrade={() => { setLikesOpen(false); onUpgrade(); }} onOpen={(p) => { setLikesOpen(false); setDetail(p); }} />
      <FiltersSheet open={filtersOpen} t={t} faNum={faNum} onClose={() => setFiltersOpen(false)}
        onApply={(prefs) => { putPreferences(prefs).then(loadDeck).catch(() => {}); }} />
    </div>
  );
}

export { DiscoverScreen, MatchOverlay, ProfileDetail };
